 // src/App.js
 import React, { useState, useEffect, useCallback, useRef } from 'react';
 import { initializeApp } from 'firebase/app';
 import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
 import { getFirestore, doc, getDoc, setDoc, writeBatch, collection, getDocs } from "firebase/firestore";
 import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
 import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider } from "firebase/auth";
 import { Home,Building2, LogOut, Crown, Zap, Infinity, Lock, Unlock, Users, RefreshCw, MapPin, Moon, Sun, AlertCircle, X, Phone, MessageCircle, Copy, Check, Settings, Share2, Edit2, Loader, Network } from 'lucide-react';
 import './App.css';
 
 const firebaseConfig = {
   apiKey: "AIzaSyCPUje363N5tCQykkXly6Fn7gI5gj0P7R4",
   authDomain: "bayantech.app",
   projectId: "bayantech-42fb2",
   storageBucket: "bayantech-42fb2.firebasestorage.app",
   messagingSenderId: "586375405452",
   appId: "1:586375405452:web:d53edbcdc62119d85e80a9",
   measurementId: "G-17VBW9761P"
 };
 
 const GOOGLE_SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTgRrFLMQuQFZwfalNDbbplUq1VhWK-UD3MLiXzC7NoQ0MaHu7e1crUyIxo4WcZQ2nmUS3_OffLCxL8/pub?output=csv";
 
 const app = initializeApp(firebaseConfig);
 const auth = getAuth(app);
 auth.languageCode = 'ar'; // Set Arabic for all auth messages including SMS
 // Persist session until explicit logout (survives tab/browser close)
 setPersistence(auth, browserLocalPersistence).catch(console.error);
 const db = getFirestore(app);
 const storage = getStorage(app);
 const googleProvider = new GoogleAuthProvider();
 
 const SUBSCRIPTION_TIERS = {
   'الأساسية': { name: 'الأساسية', points: 50, price: 0, icon: Zap, color: 'blue' },
   starter: { name: 'Starter', points: 50, price: 99, icon: Zap, color: 'blue' },
   premium: { name: 'Premium', points: 120, price: 249, icon: Crown, color: 'purple' },
   unlimited: { name: 'Unlimited', points: Infinity, price: 499, icon: Infinity, color: 'amber' }
 };
 
 const AR_LABELS = {
   appTitle: 'بَيانتِك | Bayantech',
   appSubtitle: 'منصة ذكاء العقارات',
   signIn: 'تسجيل الدخول باستخدام Google',
   loading: 'جاري التحميل...',
   plan: 'الخطة',
   points: 'النقاط',
   logout: 'تسجيل الخروج',
   subscription: 'الاشتراك',
   availableOpportunities: 'الطلبات المتاحة',
   showing: 'عرض',
   opportunities_plural: 'طلبات',
   refresh: 'تحديث',
   propertyType: 'نوع الطلب',
   location: 'الموقع',
   allTypes: 'جميع الأنواع',
   allLocations: 'جميع المواقع',
   noOpportunities: 'لم يتم العثور على طلبات',
   budget: 'الميزانية',
   users: 'مستخدمين',
   contact: 'جهة الاتصال',
   phone: 'الهاتف',
   details: 'التفاصيل',
   size: 'المساحة',
   financing: 'طريقة الدفع',
   revealContactDetails: 'الكشف عن بيانات جهة الاتصال',
   points_cost: 'نقاط',
   latest: 'الأحدث',
   oldest: 'الأقدم',
   lightMode: 'الوضع الفاتح',
   darkMode: 'الوضع الداكن',
   insufficientPoints: 'نقاط غير كافية! يرجى ترقية خطتك.',
   search: 'بحث',
   sortBy: 'ترتيب',
   callNumber: 'اتصل',
   whatsapp: 'واتس',
   copyNumber: 'نسخ',
   postedDate: 'تاريخ النشر',
   memberSince: 'عضو منذ',
   monthlyPoints: 'النقاط الشهرية',
   pointsUsed: 'النقاط المستخدمة',
   person: 'مقدم الطلب',
   allPersons: 'جميع مقدمي الطلبات',
   allFinancing: 'جميع أنواع الدفع',
   sizeRange: 'نطاق المساحة',
   share: 'مشاركة',
 };
 
 const WHATSAPP_NUMBER = '966532537860';
 const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;
const PLANS_SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTgRrFLMQuQFZwfalNDbbplUq1VhWK-UD3MLiXzC7NoQ0MaHu7e1crUyIxo4WcZQ2nmUS3_OffLCxL8/pub?gid=1630423477&single=true&output=csv";
 
 // ==================== DATE PARSING - SUPPORTS 30/01/2026 18:26:49 FORMAT ====================
 const parseDateSafely = (dateString) => {
   if (!dateString) return null;
   
   try {
     // Handle Firestore Timestamp
     if (dateString && typeof dateString === 'object' && dateString.toDate) {
       return dateString.toDate();
     }
     
     // Handle DD/MM/YYYY HH:MM:SS format (30/01/2026 18:26:49)
     if (typeof dateString === 'string') {
       const trimmed = dateString.trim();
       
       // Try DD/MM/YYYY HH:MM:SS format
       const ddmmyyyhhmmss = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/.exec(trimmed);
       if (ddmmyyyhhmmss) {
         const [, day, month, year, hours, minutes, seconds] = ddmmyyyhhmmss;
         const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
         if (!isNaN(date.getTime())) return date;
       }
       
       // Try YYYY-MM-DD HH:MM:SS format
       const yyyymmddhhmmss = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(trimmed);
       if (yyyymmddhhmmss) {
         const [, year, month, day, hours, minutes, seconds] = yyyymmddhhmmss;
         const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
         if (!isNaN(date.getTime())) return date;
       }
       
       // Try standard JavaScript date string
       const date = new Date(trimmed);
       if (!isNaN(date.getTime())) return date;
     }
     
     return null;
   } catch (error) {
     return null;
   }
 };
 
 const getTimeAgoArabic = (dateString) => {
   if (!dateString) return '';
   
   const postedDate = parseDateSafely(dateString);
   if (!postedDate) return '';
   
   const now = new Date();
   const diffMs = now - postedDate;
   
   if (isNaN(diffMs) || diffMs < 0) return '';
   
   const diffMins = Math.floor(diffMs / 60000);
   const diffHours = Math.floor(diffMs / 3600000);
   const diffDays = Math.floor(diffMs / 86400000);
 
   if (diffHours < 24) {
     if (diffMins < 1) return 'منذ أقل من دقيقة';
     if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
     if (diffHours === 1) return 'منذ ساعة';
     if (diffHours === 2) return 'منذ ساعتين';
     return `منذ ${diffHours} ساعات`;
   }
   
   if (diffDays === 1) return 'منذ يوم';
   if (diffDays === 2) return 'منذ يومين';
   if (diffDays < 30) return `منذ ${diffDays} أيام`;
   
   return `منذ ${diffDays} يوم`;
 };
 
 const getTimeAgoBadgeColor = (dateString) => {
   if (!dateString) return 'badge-default';
   
   const postedDate = parseDateSafely(dateString);
   if (!postedDate) return 'badge-default';
   
   const now = new Date();
   const diffMs = now - postedDate;
   
   if (isNaN(diffMs) || diffMs < 0) return 'badge-default';
   
   const diffHours = Math.floor(diffMs / 3600000);
   const diffDays = Math.floor(diffMs / 86400000);
 
   if (diffHours < 1) return 'badge-very-recent';
   if (diffDays === 0) return 'badge-today';
   if (diffDays <= 7) return 'badge-recent';
   return 'badge-default';
 };
 
 // ==================== FUZZY SEARCH ====================
 const calculateSimilarity = (str1, str2) => {
   const s1 = str1.toLowerCase();
   const s2 = str2.toLowerCase();
   
   if (s1 === s2) return 1;
   if (s1.includes(s2) || s2.includes(s1)) return 0.9;
   
   let matches = 0;
   for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
     if (s1[i] === s2[i]) matches++;
   }
   return matches / Math.max(s1.length, s2.length);
 };
 
 const searchOpportunities = (opps, searchQuery) => {
   if (!searchQuery || searchQuery.trim() === '') return opps;
   // Exact multi-keyword search: all terms must appear as substrings (AND logic, no fuzzy)
   const query = searchQuery.trim().toLowerCase();
   if (!query) return opps;
   // Split into terms BUT also try the full phrase — prefer full-phrase matching for multi-word queries
   const terms = query.split(/\s+/).filter(t => t.length > 0);
   return opps.filter(opp => {
     const searchFields = [
       opp.id || '',
       opp.type || '',
       opp.city || '',
       opp.district || '',
       opp.location || '',
       opp.budgetRange || '',
       opp.postedDate || '',
       opp.pointCost?.toString() || '',
       opp.contactName || '',
       opp.phone || '',
       opp.details || '',
       opp.size || '',
       opp.financing || '',
       opp.person || '',
     ].join(' ').toLowerCase();
     // ALL terms must be present as exact substrings — no fuzzy fallback
     return terms.every(term => searchFields.includes(term));
   });
 };
 

// ==================== DEVICE FINGERPRINT ====================
// Returns a stable device ID stored in localStorage (survives account changes)
const getDeviceId = () => {
  const key = 'bayan_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = 'dev_' + Math.random().toString(36).substr(2, 12) + '_' + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
};

 // ==================== MAIN APP ====================
 function App() {
   const [currentUser, setCurrentUser] = useState(null);
   const [userData, setUserData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [opportunities, setOpportunities] = useState([]);
   const [loadingOpps, setLoadingOpps] = useState(false);
   const [selectedFilters, setSelectedFilters] = useState({ type: '', location: '', person: '', financing: '' });
  const [viewsFilter, setViewsFilter] = useState(''); // 'most_viewed' | 'least_viewed'
  const [filledFilter, setFilledFilter] = useState(false); // only fully-filled opportunities
   const [availableTypes, setAvailableTypes] = useState([]);
   const [availableLocations, setAvailableLocations] = useState([]);
   const [availablePersons, setAvailablePersons] = useState([]);
   const [availableFinancing, setAvailableFinancing] = useState([]);
   const [sortBy, setSortBy] = useState('latest');
   const [searchQuery, setSearchQuery] = useState('');
   const [supportModalOpen, setSupportModalOpen] = useState(false);
   const [oppStatsCache, setOppStatsCache] = useState({});
   const [copiedPhoneId, setCopiedPhoneId] = useState(null);
   const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [currentPlanData, setCurrentPlanData] = useState(null);
  const [couponInputs, setCouponInputs] = useState({}); // {planIdx: couponCode}
  const [couponStatus, setCouponStatus] = useState({}); // {planIdx: 'valid'|'invalid'|'used'|''}
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(null); // planIdx or null
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [quickCouponCode, setQuickCouponCode] = useState('');
  const [quickCouponResult, setQuickCouponResult] = useState(null); // null | {valid: true, plan: planObj} | {valid: false}
   const [darkMode, setDarkMode] = useState(() => {
     if (typeof window === 'undefined') return false;
     const saved = localStorage.getItem('darkMode');
     return saved !== null ? JSON.parse(saved) : false;
   });
   const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
   const [submissionForm, setSubmissionForm] = useState({
     propertyType: '',
     customPropertyType: '',
     city: '',
     district: '',
     person: '',
     customPerson: '',
     contactName: '',
     mobileNumber: '',
     details: '',
     budget: '',
     paymentMethod: '',
     area: ''
   });
   const [submissionLoading, setSubmissionLoading] = useState(false);
   const [submissionSuccess, setSubmissionSuccess] = useState(false);
   
   // Profile Management
   const [editingName, setEditingName] = useState(false);
   const [tempName, setTempName] = useState('');
   const [savingName, setSavingName] = useState(false);
   const [profilePhoto, setProfilePhoto] = useState('');
   const [uploadingPhoto, setUploadingPhoto] = useState(false);
   const [phoneNumber, setPhoneNumber] = useState('');
   const [phoneNumberInput, setPhoneNumberInput] = useState('');
   const [phoneVerified, setPhoneVerified] = useState(false);
   const [phoneVerificationId, setPhoneVerificationId] = useState(null);
   const [showPhoneOtpInput, setShowPhoneOtpInput] = useState(false);
   const [phoneOtp, setPhoneOtp] = useState('');
   const [phoneError, setPhoneError] = useState('');
   const [phoneSuccess, setPhoneSuccess] = useState('');
   const [phoneSending, setPhoneSending] = useState(false);
   const [phoneVerifying, setPhoneVerifying] = useState(false);
   const [otpTimer, setOtpTimer] = useState(0);
   const [showNetworkModal, setShowNetworkModal] = useState(false);
   const [allUsers, setAllUsers] = useState([]);
   const [loadingUsers, setLoadingUsers] = useState(false);
   const [currentPage, setCurrentPage] = useState('opportunities'); // 'opportunities' | 'network'
   const [networkSearchQuery, setNetworkSearchQuery] = useState('');
   const [networkCityFilter, setNetworkCityFilter] = useState('');
   const [networkSpecFilter, setNetworkSpecFilter] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  // Login phone auth state
  const [loginMethod, setLoginMethod] = useState('google'); // 'google' | 'phone'
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [showLoginOtp, setShowLoginOtp] = useState(false);
  const [loginPhoneError, setLoginPhoneError] = useState('');
  const [loginPhoneSuccess, setLoginPhoneSuccess] = useState('');
  const [loginPhoneSending, setLoginPhoneSending] = useState(false);
  const [loginPhoneVerifying, setLoginPhoneVerifying] = useState(false);
  const [loginOtpTimer, setLoginOtpTimer] = useState(0);
  const loginOtpTimerRef = useRef(null);
   
   const fetchControllerRef = useRef(null);
   const fetchGenRef = useRef(0); // prevents stale fetch from overwriting newer results
   const otpTimerRef = useRef(null);
   const localPhotoPreviewRef = useRef('');
 
   useEffect(() => {
     if (darkMode) document.documentElement.classList.add('dark-mode');
     else document.documentElement.classList.remove('dark-mode');
     localStorage.setItem('darkMode', JSON.stringify(darkMode));
   }, [darkMode]);
 
   useEffect(() => {
     document.documentElement.lang = 'ar';
     document.documentElement.dir = 'rtl';
     document.body.dir = 'rtl';
     document.title = 'Bayantech | بيانتك';
   }, []);
 
   const loadUserData = async (uid) => {
     try {
       const userDoc = await getDoc(doc(db, 'users', uid));
       const firebaseUser = auth.currentUser;
       const authPhone = firebaseUser?.phoneNumber || null;
       const authEmail = firebaseUser?.email || null;
       const gregorianJoinDate = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

       if (userDoc.exists()) {
         const data = userDoc.data();
         const updates = {};
         if (authPhone && !data.phoneNumber) updates.phoneNumber = authPhone;
         if (authEmail && !data.email) updates.email = authEmail;
         if (Object.keys(updates).length > 0) {
           await setDoc(doc(db, 'users', uid), updates, { merge: true });
         }
         setUserData({ ...data, ...updates });
       } else {
         const newUserData = {
           email: authEmail || '',
           phoneNumber: authPhone || '',
           subscription: 'الأساسية',
           pointsUsed: 0,
           unlockedIds: [],
           role: 'broker',
           joinDate: gregorianJoinDate
         };
         await setDoc(doc(db, 'users', uid), newUserData);
         setUserData(newUserData);
       }
     } catch (error) {
       console.error('Error loading user data:', error);
     }
   };
 
   useEffect(() => {
     const unsubscribe = onAuthStateChanged(auth, async (user) => {
       if (user) {
         setCurrentUser(user);
         await loadUserData(user.uid);
       } else {
         setCurrentUser(null);
         setUserData(null);
       }
       setLoading(false);
     });
     return () => unsubscribe();
   }, []);
 
   function parseCsvRow(row) {
     const result = [];
     let current = '';
     let inQuotes = false;
     for (let i = 0; i < row.length; i++) {
       const char = row[i];
       if (char === '"') { inQuotes = !inQuotes; continue; }
       if (char === ',' && !inQuotes) { result.push(current); current = ''; } 
       else { current += char; }
     }
     result.push(current);
     return result.map(v => v.replace(/\r/g, '').trim());
   }
 
   const fetchUniqueViewerCount = async (oppId) => {
     try {
       const statsDoc = await getDoc(doc(db, 'opportunityStats', oppId));
       if (statsDoc.exists()) {
         const data = statsDoc.data();
         if (data.viewers && typeof data.viewers === 'object') {
           const uniqueEmails = new Set();
           Object.values(data.viewers).forEach(viewer => {
             if (viewer.userEmail) {
               uniqueEmails.add(viewer.userEmail);
             }
           });
           return uniqueEmails.size;
         }
       }
       return 0;
     } catch (error) {
       console.error('Error fetching viewer count for', oppId, error);
       return 0;
     }
   };
 
   // ==================== FIXED FETCH ====================
   const fetchOpportunities = useCallback(async () => {
     // Bump generation — any older in-flight fetch will see it's stale and stop
     fetchGenRef.current += 1;
     const myGen = fetchGenRef.current;
 
     try {
       setLoadingOpps(true);
       
       const timestamp = new Date().getTime();
       const response = await fetch(`${GOOGLE_SHEET_CSV_URL}&t=${timestamp}`, {
         cache: 'no-store',
         headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
       });
       
       if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
       
       // If a newer fetch started while we were waiting, discard this result
       if (myGen !== fetchGenRef.current) return;
 
       const csvText = await response.text();
       const rows = csvText.split('\n').slice(1);
       
       const parsedOpps = rows
         .filter(row => row.trim())
         .map(row => {
           const cols = parseCsvRow(row);
           return {
             id: cols[0] ? cols[0].trim() : '',
             type: cols[1] ? cols[1].trim() : '',
             city: cols[2] ? cols[2].trim() : '',
             district: cols[3] ? cols[3].trim() : '',
             budgetRange: cols[4] ? cols[4].trim() : '',
             person: cols[5] ? cols[5].trim() : '',
             contactName: cols[6] ? cols[6].trim() : '',
             phone: cols[7] ? cols[7].trim() : '',
             details: cols[8] ? cols[8].trim() : '',
             size: cols[9] ? cols[9].trim() : '',
             financing: cols[10] ? cols[10].trim() : '',
             postedDate: cols[11] ? cols[11].trim() : '',
             pointCost: parseInt(cols[12]) || 10,
             location: cols[2] && cols[3] ? `${cols[2].trim()}, ${cols[3].trim()}` : cols[2] ? cols[2].trim() : ''
           };
         })
         .filter(opp => opp.id && opp.type);
 
       const deduplicatedMap = new Map();
       parsedOpps.forEach(opp => {
         if (!deduplicatedMap.has(opp.id)) deduplicatedMap.set(opp.id, opp);
       });
       const deduplicatedOpps = Array.from(deduplicatedMap.values());
 
       if (myGen !== fetchGenRef.current) return;
 
       // Show new opportunities immediately — never reset to fewer items
       setOpportunities(prev => {
         // Only update if count increased OR data is genuinely different
         if (deduplicatedOpps.length >= prev.length) return deduplicatedOpps;
         // If sheet returned fewer results (Google Sheets propagation delay),
         // keep the larger set to avoid the count going backward
         const prevIds = new Set(prev.map(o => o.id));
         const allNew = deduplicatedOpps.every(o => prevIds.has(o.id));
         return allNew && deduplicatedOpps.length < prev.length ? prev : deduplicatedOpps;
       });
       setAvailableTypes([...new Set(deduplicatedOpps.map(o => o.type))].filter(Boolean).sort());
       // Location filter: split each district cell by Arabic/Latin commas and collect ALL individual districts
       const allDistricts = [];
       deduplicatedOpps.forEach(o => {
         if (!o.district) return;
         const parts = o.district.split(/[،,]/).map(p => p.trim()).filter(Boolean);
         parts.forEach(p => allDistricts.push(p));
       });
       setAvailableLocations([...new Set(allDistricts)].filter(Boolean).sort());
       setAvailablePersons([...new Set(deduplicatedOpps.map(o => o.person))].filter(Boolean).sort());
       setAvailableFinancing([...new Set(deduplicatedOpps.map(o => o.financing))].filter(Boolean).sort());
       setLoadingOpps(false);
 
       // Fetch viewer counts one by one and merge into existing cache
       // (so counts never flash back to 0 during a refresh)
       deduplicatedOpps.forEach(async (opp) => {
         try {
           const viewCount = await fetchUniqueViewerCount(opp.id);
           if (myGen === fetchGenRef.current) {
             setOppStatsCache(prev => ({ ...prev, [opp.id]: viewCount }));
           }
         } catch { /* keep existing count */ }
       });
 
     } catch (error) {
       if (myGen === fetchGenRef.current) {
         console.error('Error fetching opportunities:', error);
         setLoadingOpps(false);
       }
     }
   }, []);
 
   useEffect(() => {
     if (currentUser) {
       fetchOpportunities();
       fetchPlans();
     }
   }, [currentUser, fetchOpportunities]);
 
   const handleSignIn = async () => {
     try { 
       await signInWithPopup(auth, googleProvider); 
     } catch (error) { 
       console.error('Sign in error:', error); 
     }
   };
 
   const handleSignOut = async () => {
     try {
       await signOut(auth);
       setUserData(null);
       setProfilePhoto('');
       localPhotoPreviewRef.current = '';
     } catch (error) { 
       console.error('Sign out error:', error); 
     }
   };
 
   // ==================== LOGIN VIA PHONE ====================
   const handleLoginSendOtp = async () => {
     setLoginPhoneError('');
     setLoginPhoneSuccess('');
     if (!loginPhone.trim()) { setLoginPhoneError('يرجى إدخال رقم الجوال'); return; }
     const clean = loginPhone.replace(/\D/g, '');
     if (!/^05\d{8}$/.test(clean) && !/^9665\d{8}$/.test(clean)) {
       setLoginPhoneError('صيغة رقم الجوال غير صحيحة (05XXXXXXXX)');
       return;
     }
     if (loginOtpTimer > 0) { setLoginPhoneError(`انتظر ${loginOtpTimer} ثانية`); return; }
     setLoginPhoneSending(true);
     try {
       if (window.loginRecaptchaVerifier) {
         try { window.loginRecaptchaVerifier.clear(); } catch(e) {}
         window.loginRecaptchaVerifier = null;
       }
       const container = document.getElementById('login-recaptcha-container');
       if (container) container.innerHTML = '';
       await new Promise(r => setTimeout(r, 200));
       window.loginRecaptchaVerifier = new RecaptchaVerifier(auth, 'login-recaptcha-container', {
         size: 'invisible',
         'expired-callback': () => setLoginPhoneError('انتهت صلاحية التحقق، حاول مرة أخرى')
       });
       const intl = clean.startsWith('05') ? '+966' + clean.substring(1) : '+966' + clean.substring(2);
       const result = await signInWithPhoneNumber(auth, intl, window.loginRecaptchaVerifier);
       window.loginConfirmationResult = result;
       setShowLoginOtp(true);
       setLoginPhoneSuccess('تم إرسال رمز التحقق إلى هاتفك');
       setLoginOtpTimer(60);
       if (loginOtpTimerRef.current) clearInterval(loginOtpTimerRef.current);
       loginOtpTimerRef.current = setInterval(() => {
         setLoginOtpTimer(prev => { if (prev <= 1) { clearInterval(loginOtpTimerRef.current); return 0; } return prev - 1; });
       }, 1000);
     } catch(error) {
       if (error.code === 'auth/too-many-requests') setLoginPhoneError('محاولات كثيرة جداً، حاول بعد ساعة');
       else if (error.code === 'auth/invalid-phone-number') setLoginPhoneError('رقم الجوال غير صالح');
       else setLoginPhoneError('فشل إرسال الرمز، حاول مرة أخرى');
       if (window.loginRecaptchaVerifier) { try { window.loginRecaptchaVerifier.clear(); } catch(e) {} window.loginRecaptchaVerifier = null; }
     } finally { setLoginPhoneSending(false); }
   };

   const handleLoginVerifyOtp = async () => {
     setLoginPhoneError('');
     if (!loginOtp.trim() || loginOtp.length !== 6) { setLoginPhoneError('رمز التحقق يجب أن يكون 6 أرقام'); return; }
     if (!window.loginConfirmationResult) { setLoginPhoneError('حدث خطأ، أعد إرسال الرمز'); return; }
     setLoginPhoneVerifying(true);
     try {
       await window.loginConfirmationResult.confirm(loginOtp);
       window.loginConfirmationResult = null;
       if (loginOtpTimerRef.current) clearInterval(loginOtpTimerRef.current);
     } catch(error) {
       if (error.code === 'auth/invalid-verification-code') setLoginPhoneError('رمز التحقق غير صحيح');
       else if (error.code === 'auth/code-expired') { setLoginPhoneError('انتهت صلاحية الرمز، أعد الإرسال'); setShowLoginOtp(false); setLoginOtp(''); }
       else setLoginPhoneError('فشل التحقق، حاول مرة أخرى');
     } finally { setLoginPhoneVerifying(false); }
   };

   const getRemainingPoints = () => {
     if (!userData) return 0;
     // Only المميزة (featured) plans always have unlimited points
     const sub = userData.subscription || '';
     if (sub.includes('المميزة')) return Infinity;
     const tier = SUBSCRIPTION_TIERS[sub];
     if (tier) {
       if (tier.points === Infinity) return Infinity;
       return Math.max(0, tier.points - (userData.pointsUsed || 0));
     }
     // dynamic plan: use monthlyPoints stored in userData first (set on load), then currentPlanData
     const pts = (userData.monthlyPoints != null && userData.monthlyPoints > 0)
       ? userData.monthlyPoints
       : (currentPlanData ? currentPlanData.points : 0);
     if (pts === Infinity) return Infinity;
     return Math.max(0, pts - (userData.pointsUsed || 0));
   };
 
   const handleUnlockOpportunity = async (oppId, pointCost) => {
     if (!currentUser || !userData) return;
     
     if ((userData?.unlockedIds || []).includes(oppId)) return;
 
     const remainingPointsNow = getRemainingPoints();
     if (remainingPointsNow !== Infinity && remainingPointsNow < pointCost) {
       setSupportModalOpen(true);
       return;
     }
 
     try {
       const batch = writeBatch(db);
 
       const userRef = doc(db, 'users', currentUser.uid);
       const newUnlockedIds = [...(userData.unlockedIds || []), oppId];
       
       batch.update(userRef, {
         pointsUsed: (userData.pointsUsed || 0) + pointCost,
         unlockedIds: newUnlockedIds
       });
 
       const statsRef = doc(db, 'opportunityStats', oppId);
       batch.set(statsRef, {
         opportunityId: oppId,
         viewers: {
           [currentUser.email]: {
             viewedAt: new Date().toISOString(),
             userEmail: currentUser.email
           }
         },
         uniqueViewersCount: 1
       }, { merge: true });
 
       await batch.commit();
 
       setUserData(prev => ({
         ...prev,
         pointsUsed: (prev.pointsUsed || 0) + pointCost,
         unlockedIds: newUnlockedIds
       }));
 
       console.log('Successfully unlocked opportunity:', oppId);
 
     } catch (error) {
       console.error('Error unlocking:', error);
     }
   };
 
   const handleCopyPhone = (phone, oppId) => {
     navigator.clipboard.writeText(phone).then(() => {
       setCopiedPhoneId(oppId);
       setTimeout(() => setCopiedPhoneId(null), 2000);
     });
   };
 
   const handleShare = async (opp) => {
     const shareText = `
 🏢 مطلوب ${opp.type}
 📍 الموقع: ${opp.location}
 💰 الميزانية: ${opp.budgetRange && opp.budgetRange !== 'Not specified' && opp.budgetRange !== 'غير محدد' ? `${opp.budgetRange} ريال` : opp.budgetRange || 'غير محدد'}
 📐 المساحة: ${opp.size || 'غير محدد'}
 👤 مقدم الطلب: ${opp.person || 'غير محدد'}
 💳 طريقة الدفع: ${opp.financing || 'غير محدد'}
 📝 التفاصيل: ${opp.details || 'غير محدد'}
 🔗 شاهد المزيد على: https://bayantech-sa.web.app
     `.trim();
 
     if (navigator.share) {
       try {
         await navigator.share({
           title: `مطلوب ${opp.type}`,
           text: shareText,
         });
       } catch (error) {
         if (error.name !== 'AbortError') {
           console.error('Error sharing:', error);
           fallbackCopyShare(shareText);
         }
       }
     } else {
       fallbackCopyShare(shareText);
     }
   };
 
   const fallbackCopyShare = async (text) => {
     try {
       await navigator.clipboard.writeText(text);
       alert('تم نسخ التفاصيل!');
     } catch (error) {
       console.error('Failed to copy:', error);
     }
   };
 
   const handleSubmissionFormChange = (field, value) => {
     setSubmissionForm(prev => ({ ...prev, [field]: value }));
   };
 
   const resetSubmissionForm = () => {
     setSubmissionForm({
       propertyType: '',
       customPropertyType: '',
       city: '',
       district: '',
       person: '',
       customPerson: '',
       contactName: '',
       mobileNumber: '',
       details: '',
       budget: '',
       paymentMethod: '',
       area: ''
     });
     setSubmissionSuccess(false);
     setSubmissionLoading(false);
   };
 
   const handleSubmitRequest = async (e) => {
     e.preventDefault();
     
     // Validation with custom field support
     const finalPropertyType = submissionForm.propertyType === 'أخرى' ? submissionForm.customPropertyType : submissionForm.propertyType;
     const finalPerson = submissionForm.person === 'أخرى' ? submissionForm.customPerson : submissionForm.person;
     
     if (!finalPropertyType || !submissionForm.city || !submissionForm.district || 
         !finalPerson || !submissionForm.contactName || !submissionForm.mobileNumber || !submissionForm.details) {
       alert('يرجى ملء جميع الحقول المطلوبة');
       return;
     }
 
     setSubmissionLoading(true);
 
     try {
       // Generate unique request ID
       const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
       const createdAt = new Date().toLocaleString('en-GB', { 
         day: '2-digit', 
         month: '2-digit', 
         year: 'numeric', 
         hour: '2-digit', 
         minute: '2-digit', 
         second: '2-digit',
         hour12: false 
       }).replace(/,/g, '');
 
       // Prepare form data in correct column order: ID, Type, City, District, BudgetRange, Person, ContactName, Phone, Details, Size, PaymentMethod, CreatedAt, Status
       const formData = new URLSearchParams();
       formData.append('id', requestId);
       formData.append('type', finalPropertyType);
       formData.append('city', submissionForm.city);
       formData.append('district', submissionForm.district);
       formData.append('budgetRange', submissionForm.budget);
       formData.append('person', finalPerson);
       formData.append('contactName', submissionForm.contactName);
       formData.append('phone', submissionForm.mobileNumber);
       formData.append('details', submissionForm.details);
       formData.append('size', submissionForm.area);
       formData.append('paymentMethod', submissionForm.paymentMethod);
       formData.append('createdAt', createdAt);
       formData.append('status', 'new');
 
       // Submit to Google Apps Script web app
       const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzadhCKfhnkfqytoSuqmn6PE3g6yJn3QaodkyKXGZUrnYxHWFcE5g9gxonqwrGoATTBUw/exec';
       
       const response = await fetch(SCRIPT_URL, {
         method: 'POST',
         body: formData,
       });
 
       if (!response.ok) {
         throw new Error('فشل في إرسال الطلب');
       }
 
       setSubmissionSuccess(true);
       setTimeout(() => {
         setSubmissionModalOpen(false);
         resetSubmissionForm();
       }, 2000);
 
     } catch (error) {
       console.error('Error submitting request:', error);
       alert('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.');
     } finally {
       setSubmissionLoading(false);
     }
   };
 
   // ==================== FILTERING & SORTING ====================
   const isFullyFilled = (opp) => {
     const budget = opp.budgetRange ? opp.budgetRange.trim() : '';
     const budgetOk = budget.length > 1 && budget !== 'لم يحدد' && budget !== 'غير محدد' && budget !== 'Not specified';
     const detailsOk = opp.details && opp.details.trim().length > 0;
     const sizeOk = opp.size && opp.size.trim().length > 1;
     const paymentOk = opp.financing && opp.financing.trim() !== 'لم يحدد' && opp.financing.trim() !== '';
     return budgetOk && detailsOk && sizeOk && paymentOk;
   };

   const filteredAndSortedOpportunities = opportunities
     .filter(opp => {
       const typeMatch = !selectedFilters.type || (opp.type && opp.type.trim().toLowerCase() === selectedFilters.type.toLowerCase());
       const locationMatch = !selectedFilters.location || (() => {
         if (!opp.district) return false;
         // Split by Arabic or Latin comma and check if ANY segment matches
         const parts = opp.district.split(/[،,]/).map(p => p.trim()).filter(Boolean);
         return parts.some(p => p.toLowerCase() === selectedFilters.location.toLowerCase());
       })();
       const personMatch = !selectedFilters.person || (opp.person && opp.person.trim().toLowerCase() === selectedFilters.person.toLowerCase());
       const financingMatch = !selectedFilters.financing || (opp.financing && opp.financing.trim().toLowerCase() === selectedFilters.financing.toLowerCase());
       const filledMatch = !filledFilter || isFullyFilled(opp);
       return typeMatch && locationMatch && personMatch && financingMatch && filledMatch;
     })
     .sort((a, b) => {
       if (viewsFilter === 'most_viewed') return (oppStatsCache[b.id] || 0) - (oppStatsCache[a.id] || 0);
       if (viewsFilter === 'least_viewed') return (oppStatsCache[a.id] || 0) - (oppStatsCache[b.id] || 0);
       if (!a.postedDate || !b.postedDate) return 0;
       try {
         const dateA = parseDateSafely(a.postedDate);
         const dateB = parseDateSafely(b.postedDate);
         if (!dateA || !dateB) return 0;
         return sortBy === 'latest' ? dateB - dateA : dateA - dateB;
       } catch {
         return 0;
       }
     });
 
   const displayedOpportunities = searchOpportunities(filteredAndSortedOpportunities, searchQuery);
 
   const handleClearFilters = () => {
     setSelectedFilters({ type: '', location: '', person: '', financing: '' });
     setSearchQuery('');
     setSortBy('latest');
     setViewsFilter('');
     setFilledFilter(false);
   };
 
   // ===== PROFILE MANAGEMENT =====
   
   const getDisplayName = () => {
     if (userData?.name) return userData.name;
     return currentUser?.email?.split('@')[0] || 'User';
   };
 
   const getDisplayPhoto = () => {
     if (profilePhoto) return profilePhoto;
     if (userData?.photoURL) return userData.photoURL;
     return currentUser?.photoURL || '';
   };
 
   const startEditingName = () => {
     setTempName(getDisplayName());
     setEditingName(true);
   };
 
   const saveName = async () => {
     if (!tempName.trim() || tempName === getDisplayName()) {
       setEditingName(false);
       return;
     }
     
     try {
       setSavingName(true);
       const userDocRef = doc(db, 'users', currentUser.uid);
       await setDoc(userDocRef, { name: tempName.trim() }, { merge: true });
       setUserData(prev => ({ ...prev, name: tempName.trim() }));
       setEditingName(false);
     } catch (error) {
       console.error('Error saving name:', error);
       alert('Failed to save name');
     } finally {
       setSavingName(false);
     }
   };
 
   const handleNameKeyPress = (e) => {
     if (e.key === 'Enter') {
       saveName();
     } else if (e.key === 'Escape') {
       setEditingName(false);
     }
   };
 
   const handlePhotoUpload = async (e) => {
     const file = e.target.files?.[0];
     if (!file) return;
 
     if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
       alert('Please upload JPEG, PNG, or WebP image');
       return;
     }
 
     if (file.size > 5 * 1024 * 1024) {
       alert('File size must be less than 5MB');
       return;
     }
 
     // Show local preview instantly — set state before anything else
     const localPreview = URL.createObjectURL(file);
     localPhotoPreviewRef.current = localPreview;
     setProfilePhoto(localPreview);

     setUploadingPhoto(true);

     try {
       const photoRef = ref(storage, `profile-photos/${currentUser.uid}`);
       await uploadBytes(photoRef, file);
       const url = await getDownloadURL(photoRef);

       const userDocRef = doc(db, 'users', currentUser.uid);
       await setDoc(userDocRef, { photoURL: url }, { merge: true });
       localPhotoPreviewRef.current = '';
       setProfilePhoto(url);
       setUserData(prev => ({ ...prev, photoURL: url }));
     } catch (error) {
       console.error('Error uploading photo:', error);
       alert('Failed to upload photo');
     } finally {
       setUploadingPhoto(false);
     }
   };
 
   const validatePhoneNumber = (phone) => {
     const cleanPhone = phone.replace(/\D/g, '');
     const format1 = /^05\d{8}$/;
     const format2 = /^9665\d{8}$/;
     return format1.test(cleanPhone) || format2.test(cleanPhone);
   };
 
   const formatPhoneToInternal = (phone) => {
     const cleanPhone = phone.replace(/\D/g, '');
     if (cleanPhone.startsWith('05')) {
       return '+966' + cleanPhone.substring(1);
     }
     return '+966' + cleanPhone.substring(2);
   };
 
   const formatPhoneForDisplay = (phone) => {
     if (phone.startsWith('+966')) {
       return '0' + phone.substring(4);
     }
     return phone;
   };
 
   // ============ BRAND NEW PHONE AUTHENTICATION - FROM SCRATCH ============
   
   const handleSendPhoneOtp = async (e) => {
     e.preventDefault();
     setPhoneError('');
     setPhoneSuccess('');
 
     // Cooldown check
     if (otpTimer > 0) {
       setPhoneError(`يرجى الانتظار ${otpTimer} ثانية قبل إعادة الإرسال`);
       return;
     }
 
     // Validation
     if (!phoneNumberInput.trim()) {
       setPhoneError('يرجى إدخال رقم الجوال');
       return;
     }
 
     if (!validatePhoneNumber(phoneNumberInput)) {
       setPhoneError('صيغة رقم الجوال غير صحيحة (05XXXXXXXX)');
       return;
     }
 
     setPhoneSending(true);
 
     try {
       // Step 1: COMPLETELY clear any existing verifier
       if (window.recaptchaVerifier) {
         try {
           window.recaptchaVerifier.clear();
         } catch (e) {
           console.log('Clearing old verifier:', e);
         }
         window.recaptchaVerifier = null;
       }
 
       // Step 2: Clear the container
       const container = document.getElementById('phone-recaptcha-container');
       if (container) {
         container.innerHTML = '';
       }
 
       // Step 3: Wait a moment for cleanup
       await new Promise(resolve => setTimeout(resolve, 200));
 
       // Step 4: Create BRAND NEW verifier
       window.recaptchaVerifier = new RecaptchaVerifier(auth, 'phone-recaptcha-container', {
         size: 'invisible',
         callback: () => {
           console.log('✅ reCAPTCHA solved');
         },
         'expired-callback': () => {
           console.log('⏱️ reCAPTCHA expired');
           setPhoneError('انتهت صلاحية التحقق. يرجى المحاولة مرة أخرى.');
         }
       });
 
       // Step 5: Format phone number
       const phoneNumber = formatPhoneToInternal(phoneNumberInput);
       console.log('📱 Sending OTP to:', phoneNumber);
 
       // Step 6: Send OTP - this creates a NEW code
       const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
       
       // Step 7: Store confirmation result globally
       window.confirmationResult = confirmationResult;
       
       console.log('✅ OTP sent successfully!');
       setPhoneSuccess('تم إرسال رمز التحقق إلى هاتفك!');
       setShowPhoneOtpInput(true);
 
       // Step 8: Start cooldown timer
       setOtpTimer(60);
       if (otpTimerRef.current) clearInterval(otpTimerRef.current);
       otpTimerRef.current = setInterval(() => {
         setOtpTimer(prev => {
           if (prev <= 1) {
             clearInterval(otpTimerRef.current);
             return 0;
           }
           return prev - 1;
         });
       }, 1000);
 
     } catch (error) {
       console.error('❌ Error sending OTP:', error);
       
       // Cleanup on error
       if (window.recaptchaVerifier) {
         try {
           window.recaptchaVerifier.clear();
         } catch (e) {}
         window.recaptchaVerifier = null;
       }
 
       // Show user-friendly error
       if (error.code === 'auth/invalid-phone-number') {
         setPhoneError('رقم الجوال غير صالح');
       } else if (error.code === 'auth/too-many-requests') {
         setPhoneError('محاولات كثيرة جداً. يرجى المحاولة بعد ساعة.');
       } else if (error.code === 'auth/quota-exceeded') {
         setPhoneError('تم تجاوز الحد اليومي. حاول غداً.');
       } else {
         setPhoneError('فشل إرسال الرمز. حاول مرة أخرى.');
       }
     } finally {
       setPhoneSending(false);
     }
   };
 
   const handleVerifyPhoneOtp = async (e) => {
     e.preventDefault();
     setPhoneError('');
     setPhoneSuccess('');
 
     // Validation
     if (!phoneOtp.trim()) {
       setPhoneError('يرجى إدخال رمز التحقق');
       return;
     }
 
     if (phoneOtp.length !== 6) {
       setPhoneError('رمز التحقق يجب أن يكون مكون من 6 أرقام');
       return;
     }
 
     if (!window.confirmationResult) {
       setPhoneError('حدث خطأ. يرجى إعادة إرسال الرمز.');
       return;
     }
 
     setPhoneVerifying(true);
 
     try {
       console.log('🔍 Verifying OTP...');
 
       // THIS IS THE KEY: Use confirmationResult.confirm() to verify
       const result = await window.confirmationResult.confirm(phoneOtp);
       
       console.log('✅ OTP verified! User:', result.user.uid);
 
       // Save to Firestore
       const phoneNumber = formatPhoneToInternal(phoneNumberInput);
       await setDoc(doc(db, 'users', currentUser.uid), {
         phoneNumber: phoneNumber,
         phoneVerified: true,
         phoneVerifiedAt: new Date().toISOString()
       }, { merge: true });
 
       console.log('💾 Phone saved to Firestore');
 
       // Update state - STAY IN SETTINGS
       setPhoneNumber(phoneNumber);
       setPhoneVerified(true);
       setShowPhoneOtpInput(false);
       setPhoneOtp('');
       setPhoneNumberInput('');
       setPhoneSuccess('✅ تم التحقق من رقم الجوال بنجاح!');
       setUserData(prev => ({ 
         ...prev, 
         phoneNumber: phoneNumber, 
         phoneVerified: true,
         phoneVerifiedAt: new Date().toISOString()
       }));
 
       // Cleanup
       window.confirmationResult = null;
       if (otpTimerRef.current) clearInterval(otpTimerRef.current);
       setOtpTimer(0);
       if (window.recaptchaVerifier) {
         try {
           window.recaptchaVerifier.clear();
         } catch (e) {}
         window.recaptchaVerifier = null;
       }
 
     } catch (error) {
       console.error('❌ Verification error:', error);
 
       if (error.code === 'auth/invalid-verification-code') {
         setPhoneError('❌ رمز التحقق غير صحيح');
       } else if (error.code === 'auth/code-expired') {
         setPhoneError('⏱️ انتهت صلاحية الرمز. اطلب رمزاً جديداً.');
         setShowPhoneOtpInput(false);
         setPhoneOtp('');
         window.confirmationResult = null;
       } else if (error.code === 'auth/session-expired') {
         setPhoneError('⏱️ انتهت الجلسة. يرجى إعادة إرسال الرمز.');
         setShowPhoneOtpInput(false);
         setPhoneOtp('');
         window.confirmationResult = null;
       } else {
         setPhoneError('فشل التحقق. حاول مرة أخرى.');
       }
     } finally {
       setPhoneVerifying(false);
     }
   };
 
   const handleChangePhoneNumber = () => {
     // Clear everything
     if (otpTimerRef.current) clearInterval(otpTimerRef.current);
     setOtpTimer(0);
     
     if (window.recaptchaVerifier) {
       try {
         window.recaptchaVerifier.clear();
       } catch (e) {}
       window.recaptchaVerifier = null;
     }
     
     window.confirmationResult = null;
     
     setPhoneNumber('');
     setPhoneVerified(false);
     setPhoneNumberInput('');
     setShowPhoneOtpInput(false);
     setPhoneOtp('');
     setPhoneError('');
     setPhoneSuccess('');
   };
 
   const fetchAllUsers = async () => {
     setLoadingUsers(true);
     try {
       const usersCollection = collection(db, 'users');
       const usersSnapshot = await getDocs(usersCollection);
       console.log('Total docs in users collection:', usersSnapshot.size);
       const usersList = usersSnapshot.docs.map(doc => {
         const data = doc.data();
         console.log('User doc:', doc.id, data);
         return { id: doc.id, ...data };
       });
       setAllUsers(usersList);
     } catch (error) {
       console.error('Error fetching users:', error);
       alert('خطأ في جلب المستخدمين: ' + error.message);
     } finally {
       setLoadingUsers(false);
     }
   };
 
   const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(`${PLANS_SHEET_CSV_URL}&t=${timestamp}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const csvText = await response.text();
      const rows = csvText.split('\n').slice(1).filter(r => r.trim());
      const parsedPlans = rows.map(row => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < row.length; i++) {
          const char = row[i];
          if (char === '"') { inQuotes = !inQuotes; continue; }
          if (char === ',' && !inQuotes) { result.push(current); current = ''; }
          else { current += char; }
        }
        result.push(current);
        const cols = result.map(v => v.replace(/\r/g, '').trim());
        const price = parseFloat(cols[2]) || 0;
        const discount = parseFloat(cols[3]) || 0;
        const finalPrice = Math.round((price - (price * discount / 100)) * 100) / 100;
        const rawPoints = cols[1] ? cols[1].trim().toLowerCase() : '';
        const planName = cols[0] || '';
        const isFeaturedPlan = planName.includes('المميزة') || planName.toLowerCase().includes('featured');
        const pointsValue = parseInt(cols[1]) || 0;
        // Only المميزة (featured) gets unlimited points, OR if explicitly marked unlimited in the sheet
        const isUnlimited = isFeaturedPlan || rawPoints === 'unlimited' || rawPoints === 'غير محدود' || rawPoints === '∞' || rawPoints === 'infinity';
        // Columns: Name(0) Points(1) Price(2) Discount(3) Validity(4) Coupon(5) CouponValidity(6) StillValid(7)
        const stillValidRaw = (cols[7] || '').trim().toLowerCase();
        const stillValid = stillValidRaw === 'yes' || stillValidRaw === 'نعم' || stillValidRaw === 'true';
        return {
          name: planName,
          points: isUnlimited ? Infinity : (parseInt(cols[1]) || 0),
          price,
          discount,
          finalPrice,
          duration: parseInt(cols[4]) || 30,
          coupon: cols[5] ? cols[5].trim() : '',
          couponValidity: parseInt(cols[6]) || 0,
          stillValid,
          isFeatured: (cols[0] || '').includes('المميزة') || (cols[0] || '').includes('الاحترافية') || (cols[0] || '').toLowerCase().includes('featured') || (cols[0] || '').toLowerCase().includes('professional')
        };
      }).filter(p => p.name);
      setPlans(parsedPlans);
      // also update currentPlanData for the active subscription
      const active = parsedPlans.find(p => p.name === (userData?.subscription || 'الأساسية'));
      if (active) {
        setCurrentPlanData(active);
        // sync monthlyPoints into userData so getRemainingPoints works without waiting for this
        setUserData(prev => prev ? { ...prev, monthlyPoints: active.points } : prev);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Activate plan directly when a valid coupon is applied
  const activatePlanWithCoupon = async (plan, couponCode) => {
    if (!currentUser || !userData) return;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const updates = {
        subscription: plan.name,
        pointsUsed: 0,
        monthlyPoints: plan.points === Infinity ? null : plan.points,
        subscriptionActivatedAt: new Date().toISOString(),
      };
      await setDoc(userDocRef, updates, { merge: true });
      setUserData(prev => ({ ...prev, ...updates }));
      setCurrentPlanData(plan);
      // Record device usage for coupon validity tracking
      if (plan.coupon && plan.couponValidity > 0) {
        const deviceId = getDeviceId();
        const usageKey = `coupon_usage_${(couponCode || plan.coupon).trim().toLowerCase()}`;
        const usageData = JSON.parse(localStorage.getItem(usageKey) || '{}');
        usageData[deviceId] = (usageData[deviceId] || 0) + 1;
        localStorage.setItem(usageKey, JSON.stringify(usageData));
      }
    } catch (error) {
      console.error('Error activating plan:', error);
      alert('حدث خطأ أثناء تفعيل الباقة. يرجى المحاولة مرة أخرى.');
    }
  };

   const handleNetworkClick = () => {
     setCurrentPage('network');
     fetchAllUsers();
   };
 
   const loadProfileData = () => {
     if (userData?.photoURL && !profilePhoto && !localPhotoPreviewRef.current) {
       setProfilePhoto(userData.photoURL);
     }
     if (userData?.phoneNumber) {
       // Convert +9665XXXXXXXX to 05XXXXXXXX
       const raw = userData.phoneNumber;
       if (raw.startsWith('+966')) {
         setPhoneNumber('0' + raw.substring(4));
       } else {
         setPhoneNumber(raw.replace(/\D/g, ''));
       }
     }
   };
 
   // Load profile data when userData changes
   useEffect(() => {
     loadProfileData();
   }, [userData]);
 
   // Cleanup on unmount
   useEffect(() => {
     return () => {
       if (otpTimerRef.current) {
         clearInterval(otpTimerRef.current);
       }
       if (window.recaptchaVerifier) {
         try {
           window.recaptchaVerifier.clear();
         } catch (e) {}
         window.recaptchaVerifier = null;
       }
       window.confirmationResult = null;
     };
   }, []);
 
   if (loading) return (
    <div className={`loading-container ${darkMode ? 'dark' : ''}`}>
      <div className="loading-brand">
        <div className="loading-logo-ring">
          <Home size={32} />
        </div>
        <div className="loading-title">Bayantech</div>
        <div className="loading-subtitle">منصة ذكاء العقارات</div>
      </div>
      <div className="loading-progress-track">
        <div className="loading-progress-fill"></div>
      </div>
      <div className="loading-dots">
        <span></span><span></span><span></span>
      </div>
      <div className="loading-text">جاري التحميل</div>
    </div>
  );
 
   if (!currentUser) {
     return (
       <div className={`login-page ${darkMode ? 'dark' : 'light'}`}>
         <div className="login-card">
           <div className="login-header">
             <div className="login-logo">
               <Home size={30} />
             </div>
             <h1 className="app-title">{AR_LABELS.appTitle}</h1>
             <p className="app-subtitle">{AR_LABELS.appSubtitle}</p>
           </div>
           <div className="login-features">
             <div className="login-feature-item">
               <div className="login-feature-icon"><Zap size={14} /></div>
               <span>اكتشف فرص عقارية حصرية يومياً</span>
             </div>
             <div className="login-feature-item">
               <div className="login-feature-icon"><Users size={14} /></div>
               <span>تواصل مع شبكة العقاريين المحترفين</span>
             </div>
             <div className="login-feature-item">
               <div className="login-feature-icon"><Crown size={14} /></div>
               <span>نقاط مرنة لكشف بيانات التواصل</span>
             </div>
           </div>
           {/* Login Method Tabs */}
           <div className="login-tabs">
             <button
               className={`login-tab${loginMethod === 'google' ? ' active' : ''}`}
               onClick={() => { setLoginMethod('google'); setLoginPhoneError(''); setLoginPhoneSuccess(''); }}
             >
               <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
               Google
             </button>
             <button
               className={`login-tab${loginMethod === 'phone' ? ' active' : ''}`}
               onClick={() => { setLoginMethod('phone'); setLoginPhoneError(''); setLoginPhoneSuccess(''); }}
             >
               <Phone size={15} />
               رقم الجوال
             </button>
           </div>

           {loginMethod === 'google' ? (
             <button onClick={handleSignIn} className="google-signin-btn">
               <svg className="google-icon" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
               {AR_LABELS.signIn}
             </button>
           ) : (
             <div className="phone-login-form">
               <div id="login-recaptcha-container"></div>
               {!showLoginOtp ? (
                 <>
                   <div className="phone-login-input-row">
                     <input
                       type="tel"
                       inputMode="numeric"
                       placeholder="05XXXXXXXX"
                       value={loginPhone}
                       onChange={e => {
                         let v = e.target.value.replace(/\D/g, '');
                         if (v.length === 1 && v !== '0') v = '0' + v;
                         if (v.length === 2 && v[1] !== '5') v = '05';
                         if (v.length <= 10) setLoginPhone(v);
                       }}
                       className="phone-login-input"
                       maxLength="10"
                       disabled={loginPhoneSending}
                       onKeyPress={e => e.key === 'Enter' && handleLoginSendOtp()}
                     />
                     <button
                       onClick={handleLoginSendOtp}
                       disabled={loginPhoneSending || loginOtpTimer > 0}
                       className="phone-login-send-btn"
                     >
                       {loginPhoneSending ? <Loader size={18} className="spinner-icon" /> : loginOtpTimer > 0 ? `${loginOtpTimer}ث` : 'إرسال الرمز'}
                     </button>
                   </div>
                 </>
               ) : (
                 <>
                   <p className="phone-login-hint">أدخل رمز التحقق المرسل إلى {loginPhone}</p>
                   <div className="phone-login-input-row">
                     <input
                       type="tel"
                       inputMode="numeric"
                       placeholder="000000"
                       value={loginOtp}
                       onChange={e => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       className="phone-login-input otp-input"
                       maxLength="6"
                       disabled={loginPhoneVerifying}
                       onKeyPress={e => e.key === 'Enter' && handleLoginVerifyOtp()}
                       autoFocus
                     />
                     <button
                       onClick={handleLoginVerifyOtp}
                       disabled={loginPhoneVerifying || loginOtp.length !== 6}
                       className="phone-login-send-btn"
                     >
                       {loginPhoneVerifying ? <Loader size={18} className="spinner-icon" /> : 'تحقق'}
                     </button>
                   </div>
                   <button
                     className="phone-login-back"
                     onClick={() => { setShowLoginOtp(false); setLoginOtp(''); setLoginPhoneError(''); setLoginPhoneSuccess(''); }}
                   >
                     تغيير الرقم
                   </button>
                   {loginOtpTimer > 0 && <p className="phone-login-timer">إعادة الإرسال بعد {loginOtpTimer} ثانية</p>}
                   {loginOtpTimer === 0 && (
                     <button className="phone-login-back" onClick={() => { setShowLoginOtp(false); setLoginOtp(''); }}>
                       إعادة إرسال الرمز
                     </button>
                   )}
                 </>
               )}
               {loginPhoneError && <p className="phone-login-error">{loginPhoneError}</p>}
               {loginPhoneSuccess && <p className="phone-login-success">{loginPhoneSuccess}</p>}
             </div>
           )}

           <p className="login-divider">منصة آمنة ومشفرة بالكامل</p>
         </div>
       </div>
     );
   }
 
   const tier = SUBSCRIPTION_TIERS[userData?.subscription] || SUBSCRIPTION_TIERS['starter'];
   const TierIcon = tier.icon;
   const remainingPoints = getRemainingPoints();
 
   return (
     <div className={`app-container ${darkMode ? 'dark' : ''}`}>
       <header className="app-header">
         <div className="header-content">
           {/* Row 1: Logo + Title */}
           <div className="header-logo-row">
             <div className="logo-icon"><Home size={22} /></div>
             <h1 className="app-title">{AR_LABELS.appTitle}</h1>
           </div>
           {/* Row 2: Stats + Action Buttons */}
           <div className="header-actions-row">
             <div className="stat-item" style={userData?.subscription?.includes('المميزة') ? {border:'1.5px solid var(--gold)',borderRadius:'var(--r-sm)',background:'var(--gold-dim)'} : {}}><TierIcon size={15} style={userData?.subscription?.includes('المميزة') ? {color:'var(--gold)'} : {}} /><div className="stat-info"><div className="stat-label">{AR_LABELS.plan}</div><div className="stat-value" style={userData?.subscription?.includes('المميزة') ? {color:'var(--gold)',fontWeight:800} : {}}>{userData?.subscription || 'الأساسية'}</div></div></div>
             <div className="stat-item"><div className="stat-info"><div className="stat-label">{AR_LABELS.points}</div><div className="stat-value" style={userData?.subscription?.includes('المميزة') ? {color:'var(--gold)'} : {}}>{remainingPoints === Infinity ? <Infinity size={18} style={{color:'var(--gold)',verticalAlign:'middle'}} /> : remainingPoints}</div></div></div>
             <div className="header-divider"></div>
             <button onClick={() => setCurrentPage('opportunities')} className={`subscription-btn${currentPage === 'opportunities' ? ' active-page-btn' : ''}`} title="الفرص"><Home size={17} /></button>
             <button onClick={handleNetworkClick} className={`subscription-btn${currentPage === 'network' ? ' active-page-btn' : ''}`} title="الشبكة"><Network size={17} /></button>
             <button onClick={() => setShowSubscriptionModal(!showSubscriptionModal)} className="subscription-btn" title={AR_LABELS.subscription}><Settings size={17} /></button>
             <button onClick={() => setDarkMode(!darkMode)} className="mode-toggle-btn">{darkMode ? <Sun size={17} /> : <Moon size={17} />}</button>
             <button onClick={handleSignOut} className="logout-btn"><LogOut size={17} /></button>
           </div>
         </div>
       </header>
 
       {showSubscriptionModal && (
         <div className="settings-page-overlay" onClick={() => setShowSubscriptionModal(false)}>
           <div className="settings-page" onClick={e => e.stopPropagation()}>
             <div className="settings-header">
               <h2>الإعدادات</h2>
               <button onClick={() => setShowSubscriptionModal(false)} className="settings-close-btn">
                 <X size={24} />
               </button>
             </div>
 
             <div className="settings-content">
               {/* User Profile Section - UPDATED */}
               <div className="settings-section">
                 <h3 className="settings-section-title">معلومات الحساب</h3>
                 <div className="settings-card">
                   {/* Profile Photo */}
                   <div className="profile-photo-container">
                     <div className="profile-photo-wrapper">
                       <img 
                         src={getDisplayPhoto()} 
                         alt="" 
                         className="settings-avatar"
                       />
                     </div>
                   </div>
 
                   {/* Display Info */}
                   <div className="settings-info-grid">
                     {/* Name with inline edit */}
                     <div className="settings-info-item">
                       <span className="settings-label">الاسم:</span>
                       {editingName ? (
                         <div className="name-edit-input">
                           <input
                             type="text"
                             value={tempName}
                             onChange={(e) => setTempName(e.target.value)}
                             onBlur={saveName}
                             onKeyPress={handleNameKeyPress}
                             autoFocus
                             disabled={savingName}
                             className="inline-edit-input"
                           />
                           {savingName && <Loader size={16} className="inline-spinner" />}
                         </div>
                       ) : (
                         <div className="name-display">
                           <span className="settings-value">{getDisplayName()}</span>
                           <button
                             onClick={startEditingName}
                             className="edit-name-btn"
                             title="Edit name"
                           >
                             <Edit2 size={16} />
                           </button>
                         </div>
                       )}
                     </div>
 
                     {/* Email */}
                     <div className="settings-info-item">
                       <span className="settings-label">البريد الإلكتروني:</span>
                       <div className="name-display">
                         <span className="settings-value">{userData?.email || currentUser.email || 'غير متوفر'}</span>
                         <button
                           onClick={() => {
                             const newEmail = window.prompt('أدخل عنوان البريد الإلكتروني:', userData?.email || currentUser.email || '');
                             if (newEmail && newEmail.trim() && newEmail.includes('@')) {
                               setDoc(doc(db, 'users', currentUser.uid), { email: newEmail.trim() }, { merge: true })
                                 .then(() => setUserData(prev => ({ ...prev, email: newEmail.trim() })))
                                 .catch(err => console.error('Error saving email:', err));
                             }
                           }}
                           className="edit-name-btn"
                           title="تعديل البريد"
                         >
                           <Edit2 size={16} />
                         </button>
                       </div>
                     </div>
 
                     {/* Join Date */}
                     <div className="settings-info-item">
                       <span className="settings-label">عضو منذ:</span>
                       <span className="settings-value">
                         {userData?.joinDate
                           ? (() => {
                               // Re-format any stored date to Gregorian
                               const months = { 'يناير':1,'فبراير':2,'مارس':3,'أبريل':4,'مايو':5,'يونيو':6,'يوليو':7,'أغسطس':8,'سبتمبر':9,'أكتوبر':10,'نوفمبر':11,'ديسمبر':12 };
                               // If already Gregorian (contains English letters) just show it
                               if (/[a-zA-Z]/.test(userData.joinDate)) return userData.joinDate;
                               return userData.joinDate;
                             })()
                           : 'غير متوفر'}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>
 
               {/* Phone and Bio Section */}
               <div className="settings-section">
                 <h3 className="settings-section-title">معلومات التواصل</h3>
                 <div className="settings-card">
                   <div className="settings-info-grid">
                     {/* Phone Number */}
                     <div className="settings-info-item" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '8px'}}>
                       <label className="settings-label">رقم الجوال (صيغة سعودية)</label>
                       <input
                         type="tel"
                         inputMode="numeric"
                         value={phoneNumber}
                         onChange={(e) => {
                           let value = e.target.value.replace(/\D/g, '');
                           if (value.length === 1 && value !== '0') value = '0' + value;
                           if (value.length === 2 && value[1] !== '5') value = '05';
                           if (value.length <= 10) setPhoneNumber(value);
                         }}
                         onBlur={async () => {
                           if (phoneNumber && phoneNumber.length === 10 && phoneNumber.startsWith('05')) {
                             const intl = '+966' + phoneNumber.substring(1);
                             try {
                               await setDoc(doc(db, 'users', currentUser.uid), {
                                 phoneNumber: intl
                               }, { merge: true });
                               setUserData(prev => ({ ...prev, phoneNumber: intl }));
                             } catch (error) {
                               console.error('Error saving phone:', error);
                             }
                           }
                         }}
                         placeholder="05XXXXXXXX"
                         className="form-input"
                         maxLength="10"
                         style={{width: '100%', letterSpacing: '1px'}}
                       />
                       <small className="phone-hint">10 أرقام تبدأ بـ 05</small>
                       {phoneNumber && phoneNumber.length === 10 && phoneNumber.startsWith('05') && (
                         <div style={{color: 'var(--success-color)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                           <Check size={16} />
                           <span>{phoneNumber.substring(0,4)} {phoneNumber.substring(4,7)} {phoneNumber.substring(7,10)}</span>
                         </div>
                       )}
                     </div>
 
                     {/* Bio */}
                     <div className="settings-info-item" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '8px'}}>
                       <label className="settings-label">نبذة عنك</label>
                       <textarea
                         value={userData?.bio || ''}
                         onChange={(e) => {
                           setUserData(prev => ({ ...prev, bio: e.target.value }));
                         }}
                         onBlur={async () => {
                           try {
                             await setDoc(doc(db, 'users', currentUser.uid), {
                               bio: userData?.bio || ''
                             }, { merge: true });
                           } catch (error) {
                             console.error('Error saving bio:', error);
                           }
                         }}
                         placeholder="أدخل نبذة مختصرة عنك..."
                         className="form-input"
                         rows="3"
                         style={{width: '100%', resize: 'vertical'}}
                       />
                     </div>
                   </div>
                 </div>
               </div>
 
               {/* Role Section */}
               <div className="settings-section">
                 <h3 className="settings-section-title">نوع الحساب</h3>
                 <div className="settings-card">
                   <p style={{fontSize:'13px', color:'var(--text-2)', marginBottom:'12px'}}>
                     اختر نوع حسابك
                   </p>
                   <div style={{display:'flex', gap:'10px'}}>
                     {[
                       { value: 'broker', label: 'وسيط عقاري', desc: 'يظهر في الشبكة' },
                       { value: 'researcher', label: 'باحث', desc: 'لا يظهر في الشبكة' }
                     ].map(opt => (
                       <button
                         key={opt.value}
                         onClick={async () => {
                           if ((userData?.role || 'broker') === opt.value) return;
                           setSavingRole(true);
                           try {
                             await setDoc(doc(db, 'users', currentUser.uid), { role: opt.value }, { merge: true });
                             setUserData(prev => ({ ...prev, role: opt.value }));
                           } catch(err) { console.error(err); }
                           finally { setSavingRole(false); }
                         }}
                         disabled={savingRole}
                         style={{
                           flex: 1,
                           padding: '12px',
                           borderRadius: 'var(--r-md)',
                           border: (userData?.role || 'broker') === opt.value ? '2px solid var(--gold)' : '1.5px solid var(--border)',
                           background: (userData?.role || 'broker') === opt.value ? 'var(--gold-dim)' : 'var(--surface-2)',
                           color: (userData?.role || 'broker') === opt.value ? 'var(--gold)' : 'var(--text-2)',
                           cursor: savingRole ? 'not-allowed' : 'pointer',
                           fontFamily: 'Tajawal, sans-serif',
                           fontWeight: 700,
                           fontSize: '14px',
                           transition: 'var(--t-base)',
                           textAlign: 'center'
                         }}
                       >
                         <div>{opt.label}</div>
                         <div style={{fontSize:'11px', fontWeight:400, marginTop:'3px', opacity:0.7}}>{opt.desc}</div>
                       </button>
                     ))}
                   </div>
                 </div>
               </div>

               {/* Subscription Section */}
               <div className="settings-section">
                 <h3 className="settings-section-title">الاشتراك</h3>
                 <div className="settings-card">
                   <div className="subscription-tier-display">
                     <Crown size={32} className="tier-icon-amber" />
                     <div>
                       <h4 className="subscription-tier-name">{userData?.subscription || 'الأساسية'}</h4>
                       <p className="subscription-tier-price">
                         {currentPlanData && currentPlanData.finalPrice > 0 ? `${currentPlanData.finalPrice} ريال / شهرياً` : ''}
                       </p>
                     </div>
                   </div>
                   <div className="settings-info-grid">
                     <div className="settings-info-item">
                       <span className="settings-label">النقاط الشهرية:</span>
                       <span className="settings-value">
                         {(() => {
                           const points = (userData?.monthlyPoints != null && userData?.monthlyPoints > 0)
                             ? userData.monthlyPoints
                             : (currentPlanData
                               ? currentPlanData.points
                               : (SUBSCRIPTION_TIERS[userData?.subscription]?.points ?? 0));
                           return points === Infinity ? '∞ غير محدود' : points;
                         })()}
                       </span>
                     </div>
                     <div className="settings-info-item">
                       <span className="settings-label">النقاط المستخدمة:</span>
                       <span className="settings-value">{userData?.pointsUsed || 0}</span>
                     </div>
                     <div className="settings-info-item">
                       <span className="settings-label">النقاط المتبقية:</span>
                       <span className="settings-value">{remainingPoints === Infinity ? '∞' : remainingPoints}</span>
                     </div>
                   </div>
                   <button className="upgrade-plan-btn" onClick={() => { fetchPlans(); setShowPlansModal(true); }}>
                     <Crown size={18} />
                     {userData?.subscription && userData.subscription !== 'الأساسية' ? 'تغيير الباقة' : 'ترقية الباقة'}
                   </button>

                   {userData?.subscription && userData.subscription !== 'الأساسية' && (
                     <button
                       className="upgrade-plan-btn"
                       style={{ marginTop: '10px', background: 'var(--surface-2)', color: 'var(--text-2)', border: '1.5px solid var(--border)' }}
                       onClick={() => {
                         window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('مرحباً، أريد إلغاء اشتراكي الحالي والرجوع إلى الباقة الأساسية')}`, '_blank');
                       }}
                     >
                       <X size={18} />
                       إلغاء الاشتراك
                     </button>
                   )}
                 </div>
               </div>
 
               {/* Alert Settings Section - Placeholder for future feature */}
               <div className="settings-section">
                 <h3 className="settings-section-title">تنبيهات الفرص</h3>
                 <div className="settings-card">
                   <p className="settings-placeholder">قريباً: سيتمكن المستخدمون من إعداد تنبيهات مخصصة بناءً على نوع الطلب، المدينة، والحي.</p>
                   <div className="settings-alert-preview">
                     <AlertCircle size={20} />
                     <span>ميزة التنبيهات قيد التطوير</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
 
       <div className="main-container">
         {currentPage === 'network' ? (
           <NetworkPage
             allUsers={allUsers}
             loadingUsers={loadingUsers}
             networkSearchQuery={networkSearchQuery}
             setNetworkSearchQuery={setNetworkSearchQuery}
             networkCityFilter={networkCityFilter}
             setNetworkCityFilter={setNetworkCityFilter}
             networkSpecFilter={networkSpecFilter}
             setNetworkSpecFilter={setNetworkSpecFilter}
             onBack={() => setCurrentPage('opportunities')}
             fetchAllUsers={fetchAllUsers}
           />
         ) : (
         <div className="opportunities-section">
           <div className="section-header">
             <div><h2 className="section-title">{AR_LABELS.availableOpportunities}</h2><p className="section-subtitle">{AR_LABELS.showing} {displayedOpportunities.length} {AR_LABELS.opportunities_plural}</p></div>
             <button onClick={fetchOpportunities} disabled={loadingOpps} className="refresh-btn"><RefreshCw size={16} className={loadingOpps ? 'spinning' : ''} />{AR_LABELS.refresh}</button>
           </div>
 
           <div className="filters-card">
             <div className="filters-grid">
               <div className="filter-group">
                 <label className="filter-label">{AR_LABELS.propertyType}</label>
                 <select value={selectedFilters.type} onChange={(e) => setSelectedFilters(prev => ({ ...prev, type: e.target.value }))} className="filter-select">
                   <option value="">{AR_LABELS.allTypes}</option>
                   {availableTypes.map(type => <option key={type} value={type}>{type}</option>)}
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">{AR_LABELS.location}</label>
                 <select value={selectedFilters.location} onChange={(e) => setSelectedFilters(prev => ({ ...prev, location: e.target.value }))} className="filter-select">
                   <option value="">{AR_LABELS.allLocations}</option>
                   {availableLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">{AR_LABELS.person}</label>
                 <select value={selectedFilters.person} onChange={(e) => setSelectedFilters(prev => ({ ...prev, person: e.target.value }))} className="filter-select">
                   <option value="">{AR_LABELS.allPersons}</option>
                   {availablePersons.map(person => <option key={person} value={person}>{person}</option>)}
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">{AR_LABELS.financing}</label>
                 <select value={selectedFilters.financing} onChange={(e) => setSelectedFilters(prev => ({ ...prev, financing: e.target.value }))} className="filter-select">
                   <option value="">{AR_LABELS.allFinancing}</option>
                   {availableFinancing.map(financing => <option key={financing} value={financing}>{financing}</option>)}
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">{AR_LABELS.sortBy}</label>
                 <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
                   <option value="latest">{AR_LABELS.latest}</option>
                   <option value="oldest">{AR_LABELS.oldest}</option>
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">المشاهدات</label>
                 <select value={viewsFilter} onChange={(e) => setViewsFilter(e.target.value)} className="filter-select">
                   <option value="">الكل</option>
                   <option value="most_viewed">الأعلى مشاهدة</option>
                   <option value="least_viewed">الأقل مشاهدة</option>
                 </select>
               </div>
               <div className="filter-group">
                 <label className="filter-label">اكتمال البيانات</label>
                 <select value={filledFilter ? 'filled' : ''} onChange={(e) => setFilledFilter(e.target.value === 'filled')} className="filter-select">
                   <option value="">الكل</option>
                   <option value="filled">طلبات مكتملة فقط</option>
                 </select>
               </div>
             </div>
             
             <div className="search-and-clear">
               <input 
                 type="text" 
                 placeholder={AR_LABELS.search}
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                 className="search-input"
               />
               {(selectedFilters.type || selectedFilters.location || selectedFilters.person || selectedFilters.financing || searchQuery || sortBy !== 'latest' || viewsFilter || filledFilter) && (
                 <button onClick={handleClearFilters} className="clear-filters-btn">
                   <X size={16} /> إلغاء
                 </button>
               )}
             </div>
             
             <button 
               className="add-request-btn" 
               onClick={() => setSubmissionModalOpen(true)}
             >
               <Building2 size={18} />
               إضافة طلبك
             </button>
           </div>
 
           <div className="opportunities-grid">
             {displayedOpportunities.length === 0 ? (
               <div className="no-opportunities-message" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
                 <p>{AR_LABELS.noOpportunities}</p>
               </div>
             ) : (
               displayedOpportunities.map(opp => {
                 const isUnlocked = (userData?.unlockedIds || []).includes(opp.id);
                 const uniqueViewCount = oppStatsCache[opp.id] || 0;
                 const timeAgoText = getTimeAgoArabic(opp.postedDate);
                 const badgeColorClass = getTimeAgoBadgeColor(opp.postedDate);
                 
                 return (
                   <div key={opp.id} className="opp-card">
                     {/* Top row: badge (right) + viewer count (left) — always in flow, never absolute */}
                     <div className="card-top-row">
                       {timeAgoText
                         ? <div className={`time-badge ${badgeColorClass}`}>{timeAgoText}</div>
                         : <span />
                       }
                       <div className="viewer-count-inline"><Users size={11} /><span>{uniqueViewCount}</span></div>
                     </div>
                     {/* Title row */}
                     <div className="opp-header-compact">
                       <div className={`lock-status-indicator-inline ${isUnlocked ? 'unlocked' : 'locked'}`}>
                         {isUnlocked ? <Unlock size={14} /> : <Lock size={14} />}
                       </div>
                       <h3 className="opp-type-compact">مطلوب {opp.type}</h3>
                     </div>
 
                     <div className="opp-location-compact"><MapPin size={14} /><span>{opp.location}</span></div>
 
                     <div className="opp-info-compact">
                       <div className="info-row-compact">
                         <span className="info-label-compact">{AR_LABELS.budget}:</span>
                         <span className="info-value-compact">
                           {opp.budgetRange && opp.budgetRange !== 'Not specified' && opp.budgetRange !== 'غير محدد' && opp.budgetRange !== 'لم يحدد' ? `${opp.budgetRange} ريال` : (opp.budgetRange || 'غير محدد')}
                         </span>
                       </div>
                       
                       {opp.size && (
                         <div className="info-row-compact">
                           <span className="info-label-compact">{AR_LABELS.size}:</span>
                           <span className="info-value-compact">{opp.size}</span>
                         </div>
                       )}
 
                       {opp.person && (
                         <div className="info-row-compact">
                           <span className="info-label-compact">{AR_LABELS.person}:</span>
                           <span className="info-value-compact">{opp.person}</span>
                         </div>
                       )}
 
                       {opp.details && (
                         <div className="info-row-compact">
                           <span className="info-label-compact">{AR_LABELS.details}:</span>
                           <span className="info-value-compact details-text non-selectable">{opp.details}</span>
                         </div>
                       )}
                       
 
                       <div className="info-row-compact financing-with-share">
   <span className="info-label-compact">{AR_LABELS.financing}:</span>
   <span className="info-value-compact">{opp.financing}</span>
   <button 
     className="share-btn-compact share-btn-bottom" 
     onClick={() => handleShare(opp)}
     title={AR_LABELS.share}
   >
     <Share2 size={14} />
   </button>
 </div>
                     </div>
 
                     {isUnlocked && (
                       <div className="contact-section-compact">
                         <div className="contact-item-compact">
                           <span className="contact-label-compact">{AR_LABELS.contact}:</span>
                           <span className="contact-value-compact">{opp.contactName}</span>
                         </div>
                         <div className="contact-item-compact">
                           <span className="contact-label-compact">{AR_LABELS.phone}:</span>
                           <div className="phone-row">
                             <span className="contact-value-compact">{opp.phone}</span>
                             <div className="phone-buttons">
                               <button 
                                 className="phone-action-btn call-btn" 
                                 onClick={() => window.location.href = `tel:${opp.phone}`}
                                 title={AR_LABELS.callNumber}
                               >
                                 <Phone size={14} />
                               </button>
                               <button 
                                 className="phone-action-btn whatsapp-btn" 
                                 onClick={() => window.open(`https://wa.me/${opp.phone.replace(/[^\d+]/g, '')}`, '_blank')}
                                 title={AR_LABELS.whatsapp}
                               >
                                 <MessageCircle size={14} />
                               </button>
                               <button 
                                 className="phone-action-btn copy-btn" 
                                 onClick={() => handleCopyPhone(opp.phone, opp.id)}
                                 title={AR_LABELS.copyNumber}
                               >
                                 {copiedPhoneId === opp.id ? <Check size={14} /> : <Copy size={14} />}
                               </button>
                             </div>
                           </div>
                         </div>
                       </div>
                     )}
 
                     {!isUnlocked && (
                       <div className="contact-section-compact">
                         <div className="contact-item-compact">
                           <span className="contact-label-compact">{AR_LABELS.contact}:</span>
                           <span className="contact-value-compact blurred">اسم جهة الاتصال</span>
                         </div>
                         <div className="contact-item-compact">
                           <span className="contact-label-compact">{AR_LABELS.phone}:</span>
                           <span className="contact-value-compact blurred">05xxxxxxxx</span>
                         </div>
                       </div>
                     )}
 
                     {!isUnlocked && (
                       <button onClick={() => handleUnlockOpportunity(opp.id, opp.pointCost)} className="unlock-btn-compact">
                         <Unlock size={16} /> {AR_LABELS.revealContactDetails} ({opp.pointCost})
                       </button>
                     )}
                   </div>
                 );
               })
             )}
           </div>
         </div>
         )}
       </div>
 
       {supportModalOpen && (
         <div className="modal-overlay" onClick={() => setSupportModalOpen(false)}>
           <div className="modal-content" onClick={e => e.stopPropagation()}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
               <AlertCircle size={28} style={{ color: '#f59e0b' }} />
               <h2 style={{ margin: 0 }}>نقاط غير كافية</h2>
             </div>
             <p>ليس لديك نقاط كافية لكشف بيانات الاتصال. يرجى ترقية خطتك.</p>
             <div className="modal-actions">
               <button onClick={() => setSupportModalOpen(false)} className="close-btn">إغلاق</button>
               <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="whatsapp-btn-modal">ترقية الخطة</a>
             </div>
           </div>
         </div>
       )}
 
       {submissionModalOpen && (
         <div className="modal-overlay" onClick={() => { setSubmissionModalOpen(false); resetSubmissionForm(); }}>
           <div className="submission-modal-content" onClick={e => e.stopPropagation()}>
             <div className="submission-modal-header">
               <h2>إضافة طلب جديد</h2>
               <button className="modal-close-btn" onClick={() => { setSubmissionModalOpen(false); resetSubmissionForm(); }}>
                 <X size={20} />
               </button>
             </div>
             
             {submissionSuccess ? (
               <div className="submission-success">
                 <Check size={48} style={{ color: '#10b981' }} />
                 <h3>تم إرسال الطلب بنجاح!</h3>
                 <p>سيتم مراجعة طلبك قريباً</p>
               </div>
             ) : (
               <form onSubmit={handleSubmitRequest} className="submission-form">
                 <div className="form-section">
                   <h3 className="form-section-title">معلومات الطلب</h3>
                   
                   <div className="form-group">
                     <label className="form-label">نوع الطلب *</label>
                     <select 
                       className="form-input" 
                       value={submissionForm.propertyType}
                       onChange={(e) => handleSubmissionFormChange('propertyType', e.target.value)}
                       required
                     >
                       <option value="">اختر نوع الطلب</option>
                       {availableTypes.map(type => (
                         <option key={type} value={type}>{type}</option>
                       ))}
                       <option value="أخرى">أخرى</option>
                     </select>
                   </div>
 
                   {submissionForm.propertyType === 'أخرى' && (
                     <div className="form-group">
                       <label className="form-label">حدد نوع الطلب *</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.customPropertyType}
                         onChange={(e) => handleSubmissionFormChange('customPropertyType', e.target.value)}
                         placeholder="اكتب نوع الطلب"
                         required
                       />
                     </div>
                   )}
 
                   <div className="form-row">
                     <div className="form-group">
                       <label className="form-label">المدينة *</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.city}
                         onChange={(e) => handleSubmissionFormChange('city', e.target.value)}
                         required
                       />
                     </div>
                     <div className="form-group">
                       <label className="form-label">الحي *</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.district}
                         onChange={(e) => handleSubmissionFormChange('district', e.target.value)}
                         required
                       />
                     </div>
                   </div>
 
                   <div className="form-row">
                     <div className="form-group">
                       <label className="form-label">الميزانية (ريال)</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.budget}
                         onChange={(e) => handleSubmissionFormChange('budget', e.target.value)}
                         placeholder="اختياري"
                       />
                     </div>
                     <div className="form-group">
                       <label className="form-label">المساحة (م²)</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.area}
                         onChange={(e) => handleSubmissionFormChange('area', e.target.value)}
                         placeholder="اختياري"
                       />
                     </div>
                   </div>
 
                   <div className="form-group">
                     <label className="form-label">طريقة الدفع</label>
                     <select 
                       className="form-input" 
                       value={submissionForm.paymentMethod}
                       onChange={(e) => handleSubmissionFormChange('paymentMethod', e.target.value)}
                     >
                       <option value="">اختياري</option>
                       <option value="كاش">كاش</option>
                       <option value="تمويل">تمويل</option>
                       <option value="لم يحدد">لم يحدد</option>
                     </select>
                   </div>
 
                   <div className="form-group">
                     <label className="form-label">التفاصيل (عن العقار) *</label>
                     <textarea 
                       className="form-textarea" 
                       rows="4"
                       value={submissionForm.details}
                       onChange={(e) => handleSubmissionFormChange('details', e.target.value)}
                       placeholder="أدخل تفاصيل إضافية عن العقار المطلوب"
                       required
                     />
                   </div>
                 </div>
 
                 <div className="form-section">
                   <h3 className="form-section-title">معلومات مقدم الطلب</h3>
                   
                   <div className="form-group">
                     <label className="form-label">صفة مقدم الطلب *</label>
                     <select 
                       className="form-input" 
                       value={submissionForm.person}
                       onChange={(e) => handleSubmissionFormChange('person', e.target.value)}
                       required
                     >
                       <option value="">اختر الصفة</option>
                       {availablePersons.map(person => (
                         <option key={person} value={person}>{person}</option>
                       ))}
                       <option value="أخرى">أخرى</option>
                     </select>
                   </div>
 
                   {submissionForm.person === 'أخرى' && (
                     <div className="form-group">
                       <label className="form-label">حدد صفة مقدم الطلب *</label>
                       <input 
                         type="text" 
                         className="form-input" 
                         value={submissionForm.customPerson}
                         onChange={(e) => handleSubmissionFormChange('customPerson', e.target.value)}
                         placeholder="اكتب صفة مقدم الطلب"
                         required
                       />
                     </div>
                   )}
 
                   <div className="form-group">
                     <label className="form-label">اسم جهة الاتصال *</label>
                     <input 
                       type="text" 
                       className="form-input" 
                       value={submissionForm.contactName}
                       onChange={(e) => handleSubmissionFormChange('contactName', e.target.value)}
                       required
                     />
                   </div>
 
                   <div className="form-group">
   <label className="form-label">رقم الجوال *</label>
   <input 
     type="tel" 
     className="form-input" 
     value={submissionForm.mobileNumber}
     onChange={(e) => handleSubmissionFormChange('mobileNumber', e.target.value)}
     placeholder="05xxxxxxxx"
     pattern="05[0-9]{8}"
     title="يجب أن يبدأ الرقم بـ 05 ويتكون من 10 أرقام"
     maxLength="10"
     required
   />
 </div>
                 </div>
 
                 <div className="form-actions">
                   <button 
                     type="button" 
                     className="form-btn-secondary" 
                     onClick={() => { setSubmissionModalOpen(false); resetSubmissionForm(); }}
                     disabled={submissionLoading}
                   >
                     إلغاء
                   </button>
                   <button 
                     type="submit" 
                     className="form-btn-primary" 
                     disabled={submissionLoading}
                   >
                     {submissionLoading ? 'جاري الإرسال...' : 'إرسال الطلب'}
                   </button>
                 </div>
               </form>
             )}
           </div>
         </div>
       )}
 
       {/* Network Modal removed - replaced with full page */}

      {/* Plans Modal */}
      {showPlansModal && (
        <div className="settings-page-overlay" onClick={() => { setShowPlansModal(false); setCouponInputs({}); setCouponStatus({}); }}>
          <div className="settings-page" onClick={e => e.stopPropagation()} style={{maxWidth:'520px'}}>
            <div className="settings-header">
              <h2>الباقات المتاحة</h2>
              <button onClick={() => { setShowPlansModal(false); setCouponInputs({}); setCouponStatus({}); setSubscriptionSuccess(null); }} className="settings-close-btn"><X size={24} /></button>
            </div>
            <div className="settings-content">
              {loadingPlans ? (
                <div style={{display:'flex',justifyContent:'center',padding:'40px'}}><Loader size={32} className="spinner-icon" /></div>
              ) : plans.length === 0 ? (
                <div style={{textAlign:'center',padding:'40px',color:'var(--text-2)'}}>لا توجد باقات متاحة</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
                  {plans.map((plan, idx) => {
                    const isActive = (userData?.subscription || 'الأساسية') === plan.name;
                    const isFeatured = plan.isFeatured || plan.name.includes('المميزة') || plan.name.includes('الاحترافية');
                    const couponVal = couponInputs[idx] || '';
                    const couponStat = couponStatus[idx] || '';
                    const isSubSuccess = subscriptionSuccess === idx;
                    const planPointsDisplay = plan.points === Infinity ? '∞' : plan.points;
                    // Build WA message with coupon if entered
                    const waText = couponVal
                      ? `مرحباً، أريد الاشتراك في باقة "${plan.name}" بسعر ${plan.finalPrice} ريال باستخدام الكوبون: ${couponVal}`
                      : `مرحباً، أريد الاشتراك في باقة "${plan.name}" بسعر ${plan.finalPrice} ريال`;
                    const waMsg = encodeURIComponent(waText);

                    // Validate coupon against plan's coupon field
                    const handleCouponCheck = () => {
                      if (!couponVal.trim()) return;
                      // Check stillValid flag first
                      if (!plan.stillValid) {
                        setCouponStatus(prev => ({...prev, [idx]: 'invalid'}));
                        return;
                      }
                      if (plan.coupon && couponVal.trim().toLowerCase() === plan.coupon.trim().toLowerCase()) {
                        // Check device usage limit
                        if (plan.couponValidity > 0) {
                          const deviceId = getDeviceId();
                          const usageKey = `coupon_usage_${plan.coupon.trim().toLowerCase()}`;
                          const usageData = JSON.parse(localStorage.getItem(usageKey) || '{}');
                          const deviceUsageCount = usageData[deviceId] || 0;
                          if (deviceUsageCount >= plan.couponValidity) {
                            setCouponStatus(prev => ({...prev, [idx]: 'exceeded'}));
                            return;
                          }
                        }
                        setCouponStatus(prev => ({...prev, [idx]: 'valid'}));
                      } else {
                        setCouponStatus(prev => ({...prev, [idx]: 'invalid'}));
                      }
                    };

                    return (
                      <div key={idx} className={`settings-card${isFeatured ? ' featured-plan-card' : ''}`} style={{
                        border: isFeatured ? '2px solid var(--gold)' : isActive ? '2px solid var(--gold)' : '1.5px solid var(--border)',
                        background: isFeatured ? 'linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(229,200,122,0.06) 100%)' : isActive ? 'var(--gold-dim)' : 'var(--surface-2)',
                        boxShadow: isFeatured ? 'var(--shadow-gold)' : undefined,
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {isFeatured && (
                          <div style={{
                            position:'absolute', top:0, left:0, right:0,
                            height:'3px',
                            background:'linear-gradient(90deg, var(--gold), var(--gold-light), var(--gold))'
                          }} />
                        )}
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <h3 style={{fontSize:'18px',fontWeight:700,color: isFeatured ? 'var(--gold)' : 'var(--text-1)',margin:0}}>{plan.name}</h3>
                              {isFeatured && <span style={{fontSize:'10px',fontWeight:700,background:'var(--gold)',color:'var(--navy)',padding:'2px 7px',borderRadius:'99px',letterSpacing:'0.5px'}}>مميزة</span>}
                            </div>
                            <p style={{fontSize:'13px',color:'var(--text-2)',marginTop:'4px',display:'flex',alignItems:'center',gap:'6px'}}>
                              {plan.points === Infinity ? (
                                <>
                                  <Infinity size={16} style={{color: isFeatured ? 'var(--gold)' : 'var(--primary-color)'}} />
                                  <span>نقاط غير محدودة</span>
                                </>
                              ) : `${planPointsDisplay} نقطة شهرياً`}
                            </p>
                          </div>
                          <div style={{textAlign:'left'}}>
                            {plan.discount > 0 && (
                              <div style={{fontSize:'12px',color:'var(--text-2)',textDecoration:'line-through'}}>{plan.price} ريال</div>
                            )}
                            <div style={{fontSize:'20px',fontWeight:800,color: isFeatured ? 'var(--gold)' : 'var(--primary-color)'}}>{plan.finalPrice} ريال</div>
                            {plan.discount > 0 && (
                              <div style={{fontSize:'11px',color:'#22c55e',fontWeight:600}}>خصم {plan.discount}%</div>
                            )}
                          </div>
                        </div>

                        {/* Coupon input for Professional & Featured paid plans */}
                        {!isActive && plan.finalPrice > 0 && !isSubSuccess && (
                          <div style={{marginBottom:'16px'}}>
                            {/* Divider */}
                            <div style={{height:'1px',background: isFeatured ? 'linear-gradient(90deg,transparent,var(--gold),transparent)' : 'var(--border)',marginBottom:'14px',opacity:0.5}} />
                            <div style={{
                              fontSize:'12px',
                              color: isFeatured ? 'var(--gold)' : 'var(--text-2)',
                              marginBottom:'8px',
                              fontWeight:700,
                              display:'flex',
                              alignItems:'center',
                              gap:'6px',
                              letterSpacing:'0.3px'
                            }}>
                              <span style={{fontSize:'15px'}}>🏷️</span>
                              {isFeatured ? 'كود الخصم الحصري (اختياري)' : 'أدخل كود الخصم (اختياري)'}
                            </div>
                            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                              <input
                                type="text"
                                placeholder="أدخل كود الخصم هنا..."
                                value={couponVal}
                                onChange={e => {
                                  setCouponInputs(prev => ({...prev, [idx]: e.target.value}));
                                  setCouponStatus(prev => ({...prev, [idx]: ''}));
                                }}
                                onKeyPress={e => e.key === 'Enter' && handleCouponCheck()}
                                style={{
                                  flex:1,
                                  width:'100%',
                                  padding:'10px 14px',
                                  border: couponStat === 'valid'
                                    ? '1.5px solid #22c55e'
                                    : couponStat === 'invalid' || couponStat === 'exceeded'
                                      ? '1.5px solid var(--danger-color)'
                                      : isFeatured
                                        ? '1.5px solid var(--gold)'
                                        : '1.5px solid var(--border)',
                                  borderRadius:'var(--r-sm)',
                                  background: isFeatured ? 'rgba(201,168,76,0.04)' : 'var(--surface)',
                                  color:'var(--text)',
                                  fontFamily:'Tajawal, sans-serif',
                                  fontSize:'14px',
                                  outline:'none',
                                  transition:'var(--t-base)',
                                  boxSizing:'border-box',
                                  boxShadow: couponStat === 'valid'
                                    ? '0 0 0 3px rgba(34,197,94,0.15)'
                                    : couponStat === 'invalid' || couponStat === 'exceeded'
                                      ? '0 0 0 3px rgba(229,57,53,0.12)'
                                      : isFeatured
                                        ? '0 0 0 2px rgba(201,168,76,0.10)'
                                        : 'none'
                                }}
                              />
                              <button
                                onClick={handleCouponCheck}
                                style={{
                                  width:'100%',
                                  padding:'11px 16px',
                                  background: isFeatured
                                    ? 'linear-gradient(135deg, var(--gold), var(--gold-light))'
                                    : 'var(--primary-color)',
                                  color: isFeatured ? 'var(--navy)' : '#fff',
                                  border:'none',
                                  borderRadius:'var(--r-sm)',
                                  fontFamily:'Tajawal, sans-serif',
                                  fontSize:'14px',
                                  fontWeight:800,
                                  cursor:'pointer',
                                  whiteSpace:'nowrap',
                                  transition:'var(--t-base)',
                                  boxShadow: isFeatured ? 'var(--shadow-gold)' : 'none',
                                  letterSpacing:'0.3px'
                                }}
                              >تحقق من الكود</button>
                            </div>
                            {couponStat === 'valid' && (
                              <div className="coupon-success-anim" style={{
                                marginTop:'10px',
                                fontSize:'13px',
                                color:'#22c55e',
                                display:'flex',
                                alignItems:'center',
                                gap:'8px',
                                padding:'10px 14px',
                                background:'rgba(34,197,94,0.08)',
                                borderRadius:'var(--r-sm)',
                                border:'1.5px solid rgba(34,197,94,0.25)',
                                fontWeight:600
                              }}>
                                <Check size={16} />
                                <span>✅ <strong>الكوبون صحيح ومفعّل!</strong> اضغط على زر الاشتراك أدناه لتطبيق الخصم.</span>
                              </div>
                            )}
                            {couponStat === 'invalid' && (
                              <div className="coupon-success-anim" style={{
                                marginTop:'10px',
                                fontSize:'13px',
                                color:'var(--danger-color)',
                                display:'flex',
                                alignItems:'center',
                                gap:'8px',
                                padding:'10px 14px',
                                background:'rgba(229,57,53,0.06)',
                                borderRadius:'var(--r-sm)',
                                border:'1.5px solid rgba(229,57,53,0.18)',
                                fontWeight:600
                              }}>
                                <X size={16} /> كود الخصم غير صحيح أو منتهي الصلاحية. يرجى التحقق من الكود وإعادة المحاولة.
                              </div>
                            )}
                            {couponStat === 'exceeded' && (
                              <div className="coupon-success-anim" style={{
                                marginTop:'10px',
                                fontSize:'13px',
                                color:'var(--danger-color)',
                                display:'flex',
                                alignItems:'center',
                                gap:'8px',
                                padding:'10px 14px',
                                background:'rgba(229,57,53,0.06)',
                                borderRadius:'var(--r-sm)',
                                border:'1.5px solid rgba(229,57,53,0.18)',
                                fontWeight:600
                              }}>
                                <X size={16} /> لقد استخدمت هذا الكوبون الحد الأقصى المسموح به على هذا الجهاز.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Subscription success state */}
                        {isSubSuccess && (
                          <div className="coupon-success-anim" style={{
                            marginBottom:'16px',
                            padding:'20px',
                            background: isFeatured
                              ? 'linear-gradient(135deg, rgba(201,168,76,0.12), rgba(229,200,122,0.06))'
                              : 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(14,169,114,0.06))',
                            borderRadius:'var(--r-md)',
                            border: isFeatured
                              ? '1.5px solid rgba(201,168,76,0.4)'
                              : '1.5px solid rgba(34,197,94,0.35)',
                            textAlign:'center',
                            boxShadow: isFeatured ? 'var(--shadow-gold)' : '0 4px 16px rgba(34,197,94,0.12)'
                          }}>
                            <div style={{fontSize:'32px',marginBottom:'8px'}}>🎉</div>
                            <div style={{
                              fontSize:'16px',
                              fontWeight:800,
                              color: isFeatured ? 'var(--gold)' : '#22c55e',
                              marginBottom:'6px',
                              letterSpacing:'0.2px'
                            }}>تم تفعيل الباقة بنجاح! 🎉</div>
                            <div style={{
                              fontSize:'13px',
                              color:'var(--text-2)',
                              lineHeight:1.6,
                              maxWidth:'280px',
                              margin:'0 auto'
                            }}>تم تفعيل باقة "{plan.name}" مباشرةً. يمكنك الآن الاستمتاع بمميزاتها.</div>
                          </div>
                        )}

                        {isActive ? (
                          <div style={{textAlign:'center',padding:'12px',color:'var(--gold)',fontWeight:700,fontSize:'14px',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',background:'var(--gold-dim)',borderRadius:'var(--r-sm)'}}>
                            <Check size={16} /> باقتك الحالية النشطة
                          </div>
                        ) : plan.finalPrice === 0 ? (
                          <div style={{textAlign:'center',padding:'12px',color:'var(--text-2)',fontWeight:600,fontSize:'14px',background:'var(--surface-2)',borderRadius:'var(--r-sm)'}}>الباقة الافتراضية المجانية</div>
                        ) : isSubSuccess ? null : (
                          couponStat === 'valid' ? (
                            <button
                              className={`upgrade-plan-btn${isFeatured ? ' featured-plan-btn' : ''}`}
                              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',width:'100%',cursor:'pointer',border:'none'}}
                              onClick={async () => {
                                await activatePlanWithCoupon(plan, couponVal);
                                setSubscriptionSuccess(idx);
                                setTimeout(() => {
                                  setSubscriptionSuccess(null);
                                  setShowPlansModal(false);
                                  setCouponInputs({});
                                  setCouponStatus({});
                                }, 3000);
                              }}
                            >
                              <Check size={16} />
                              🏷️ تفعيل الباقة الآن
                            </button>
                          ) : (
                            <a
                              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`upgrade-plan-btn${isFeatured ? ' featured-plan-btn' : ''}`}
                              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',textDecoration:'none'}}
                              onClick={() => {
                                setSubscriptionSuccess(idx);
                                setTimeout(() => setSubscriptionSuccess(null), 5000);
                              }}
                            >
                              <MessageCircle size={16} />
                              اشترك الآن عبر واتساب
                            </a>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Coupon Entry Modal */}
      {showCouponModal && (
        <div className="modal-overlay" onClick={() => setShowCouponModal(false)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()} style={{maxWidth: '500px'}}>
            <div className="settings-header">
              <button className="modal-close-btn" onClick={() => setShowCouponModal(false)}>
                <X size={20} />
              </button>
              <h2 className="settings-title">🏷️ أدخل كود الخصم</h2>
            </div>
            
            <div className="settings-content" style={{padding: '24px'}}>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-2)',
                marginBottom: '20px',
                textAlign: 'center',
                lineHeight: '1.6'
              }}>
                أدخل كود الخصم الخاص بك للتحقق من الباقة المتاحة والاشتراك مباشرة
              </p>

              <div style={{marginBottom: '20px'}}>
                <input
                  type="text"
                  placeholder="أدخل كود الخصم هنا..."
                  value={quickCouponCode}
                  onChange={e => {
                    setQuickCouponCode(e.target.value);
                    setQuickCouponResult(null);
                  }}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      const foundPlan = plans.find(p => 
                        p.coupon && p.coupon.toLowerCase() === quickCouponCode.trim().toLowerCase()
                      );
                      if (foundPlan) {
                        if (!foundPlan.stillValid) { setQuickCouponResult({valid: false, reason: 'expired'}); return; }
                        if (foundPlan.couponValidity > 0) {
                          const deviceId = getDeviceId();
                          const usageKey = `coupon_usage_${foundPlan.coupon.trim().toLowerCase()}`;
                          const usageData = JSON.parse(localStorage.getItem(usageKey) || '{}');
                          if ((usageData[deviceId] || 0) >= foundPlan.couponValidity) { setQuickCouponResult({valid: false, reason: 'exceeded'}); return; }
                        }
                        setQuickCouponResult({valid: true, plan: foundPlan});
                      } else {
                        setQuickCouponResult({valid: false, reason: 'notfound'});
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: quickCouponResult?.valid 
                      ? '2px solid #22c55e' 
                      : quickCouponResult?.valid === false 
                        ? '2px solid var(--danger-color)'
                        : '1.5px solid var(--border)',
                    borderRadius: 'var(--r-md)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    fontSize: '15px',
                    fontFamily: 'Tajawal, sans-serif',
                    outline: 'none',
                    transition: 'var(--t-base)',
                    boxShadow: quickCouponResult?.valid 
                      ? '0 0 0 3px rgba(34,197,94,0.15)'
                      : quickCouponResult?.valid === false
                        ? '0 0 0 3px rgba(229,57,53,0.12)'
                        : 'none'
                  }}
                />
              </div>

              <button
                onClick={() => {
                  if (!quickCouponCode.trim()) return;
                  const foundPlan = plans.find(p => 
                    p.coupon && p.coupon.toLowerCase() === quickCouponCode.trim().toLowerCase()
                  );
                  if (foundPlan) {
                    // Check stillValid flag
                    if (!foundPlan.stillValid) {
                      setQuickCouponResult({valid: false, reason: 'expired'});
                      return;
                    }
                    // Check device usage limit
                    if (foundPlan.couponValidity > 0) {
                      const deviceId = getDeviceId();
                      const usageKey = `coupon_usage_${foundPlan.coupon.trim().toLowerCase()}`;
                      const usageData = JSON.parse(localStorage.getItem(usageKey) || '{}');
                      const deviceUsageCount = usageData[deviceId] || 0;
                      if (deviceUsageCount >= foundPlan.couponValidity) {
                        setQuickCouponResult({valid: false, reason: 'exceeded'});
                        return;
                      }
                    }
                    setQuickCouponResult({valid: true, plan: foundPlan});
                  } else {
                    setQuickCouponResult({valid: false, reason: 'notfound'});
                  }
                }}
                disabled={!quickCouponCode.trim()}
                className="upgrade-plan-btn"
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
                  color: 'var(--navy)',
                  boxShadow: 'var(--shadow-gold)',
                  border: 'none',
                  opacity: !quickCouponCode.trim() ? 0.5 : 1,
                  cursor: !quickCouponCode.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                تحقق من الكود
              </button>

              {/* Success Result */}
              {quickCouponResult?.valid && quickCouponResult.plan && (
                <div className="coupon-success-anim" style={{
                  marginTop: '20px',
                  padding: '20px',
                  background: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                    ? 'linear-gradient(135deg, rgba(201,168,76,0.12), rgba(229,200,122,0.06))'
                    : 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(14,169,114,0.06))',
                  borderRadius: 'var(--r-md)',
                  border: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                    ? '2px solid rgba(201,168,76,0.4)'
                    : '2px solid rgba(34,197,94,0.35)',
                  boxShadow: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                    ? 'var(--shadow-gold)'
                    : '0 4px 16px rgba(34,197,94,0.12)'
                }}>
                  <div style={{fontSize: '32px', textAlign: 'center', marginBottom: '12px'}}>✅</div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 800,
                    color: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                      ? 'var(--gold)'
                      : '#22c55e',
                    textAlign: 'center',
                    marginBottom: '10px',
                    letterSpacing: '0.2px'
                  }}>
                    🎉 الكوبون صحيح!
                  </div>
                  
                  <div style={{
                    background: 'var(--surface)',
                    padding: '16px',
                    borderRadius: 'var(--r-sm)',
                    marginBottom: '16px'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                      <span style={{fontSize: '18px', fontWeight: 700, color: 'var(--text)'}}>
                        {quickCouponResult.plan.name}
                      </span>
                      {(quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')) && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          background: 'var(--gold)',
                          color: 'var(--navy)',
                          padding: '3px 8px',
                          borderRadius: '99px'
                        }}>مميزة</span>
                      )}
                    </div>
                    <div style={{fontSize: '13px', color: 'var(--text-2)', marginBottom: '8px'}}>
                      {quickCouponResult.plan.points === Infinity ? (
                        <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                          <Infinity size={16} style={{color: 'var(--gold)'}} />
                          نقاط غير محدودة
                        </span>
                      ) : `${quickCouponResult.plan.points} نقطة شهرياً`}
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      {quickCouponResult.plan.discount > 0 && (
                        <span style={{
                          fontSize: '14px',
                          color: 'var(--text-3)',
                          textDecoration: 'line-through'
                        }}>{quickCouponResult.plan.price} ريال</span>
                      )}
                      <span style={{
                        fontSize: '22px',
                        fontWeight: 800,
                        color: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                          ? 'var(--gold)'
                          : 'var(--primary-color)'
                      }}>{quickCouponResult.plan.finalPrice} ريال</span>
                      {quickCouponResult.plan.discount > 0 && (
                        <span style={{
                          fontSize: '12px',
                          color: '#22c55e',
                          fontWeight: 700,
                          background: 'rgba(34,197,94,0.1)',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}>خصم {quickCouponResult.plan.discount}%</span>
                      )}
                    </div>
                  </div>

                  <button
                    className="upgrade-plan-btn"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                        ? 'linear-gradient(135deg, var(--gold), var(--gold-light))'
                        : 'var(--primary-color)',
                      color: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                        ? 'var(--navy)'
                        : '#fff',
                      boxShadow: quickCouponResult.plan.name.includes('المميزة') || quickCouponResult.plan.name.includes('الاحترافية')
                        ? 'var(--shadow-gold)'
                        : 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={async () => {
                      await activatePlanWithCoupon(quickCouponResult.plan, quickCouponCode);
                      setShowCouponModal(false);
                      setQuickCouponCode('');
                      setQuickCouponResult(null);
                    }}
                  >
                    <Check size={16} />
                    تفعيل الباقة الآن 🏷️
                  </button>
                </div>
              )}

              {/* Error Result */}
              {quickCouponResult?.valid === false && (
                <div className="coupon-success-anim" style={{
                  marginTop: '20px',
                  padding: '16px',
                  background: 'rgba(229,57,53,0.08)',
                  borderRadius: 'var(--r-md)',
                  border: '2px solid rgba(229,57,53,0.25)',
                  textAlign: 'center'
                }}>
                  <div style={{fontSize: '32px', marginBottom: '8px'}}>❌</div>
                  <div style={{
                    fontSize: '15px',
                    color: 'var(--danger-color)',
                    fontWeight: 700,
                    marginBottom: '6px'
                  }}>
                    {quickCouponResult.reason === 'exceeded' ? 'تجاوزت الحد المسموح به' : 'كود الخصم غير صحيح'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text-2)',
                    lineHeight: '1.6'
                  }}>
                    {quickCouponResult.reason === 'exceeded'
                      ? 'لقد استخدمت هذا الكوبون الحد الأقصى المسموح به على هذا الجهاز.'
                      : quickCouponResult.reason === 'expired'
                        ? 'هذا الكوبون غير مفعّل حالياً. يرجى التواصل مع فريق الدعم.'
                        : 'يرجى التحقق من الكود والمحاولة مرة أخرى، أو التواصل مع فريق الدعم للمساعدة.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

       {/* Footer */}
       <footer className="app-footer">
         <p>© {new Date().getFullYear()} Bayantech · بَيانتِك | جميع الحقوق محفوظة</p>
       </footer>
     </div>
   );
 }
 
 // ==================== NETWORK PAGE COMPONENT ====================
 function NetworkPage({ allUsers, loadingUsers, networkSearchQuery, setNetworkSearchQuery, networkCityFilter, setNetworkCityFilter, networkSpecFilter, setNetworkSpecFilter, onBack, fetchAllUsers }) {
   
   const availableSubscriptions = [...new Set(allUsers.map(u => u.subscription).filter(Boolean))].sort();
 
   const filteredUsers = allUsers.filter(user => {
     const roleMatch = (user.role || 'broker') === 'broker';
     const subMatch = !networkCityFilter || (user.subscription && user.subscription === networkCityFilter);
     const searchMatch = !networkSearchQuery || [
       user.name, user.bio
     ].filter(Boolean).join(' ').toLowerCase().includes(networkSearchQuery.toLowerCase());
     return roleMatch && subMatch && searchMatch;
   });
 
   const handleClearNetworkFilters = () => {
     setNetworkCityFilter('');
     setNetworkSpecFilter('');
     setNetworkSearchQuery('');
   };
 
   const hasActiveFilters = networkCityFilter || networkSearchQuery;
 
   const SUBSCRIPTION_LABELS = {
     starter: 'Starter',
     premium: 'Premium',
     unlimited: 'Unlimited'
   };
 
   return (
     <div className="opportunities-section">
       <div className="section-header">
         <div>
           <h2 className="section-title">شبكة العقاريين</h2>
           <p className="section-subtitle">عرض {filteredUsers.length} عضو</p>
         </div>
         <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
           <button onClick={fetchAllUsers} disabled={loadingUsers} className="refresh-btn">
             <RefreshCw size={16} className={loadingUsers ? 'spinning' : ''} />
             تحديث
           </button>

         </div>
       </div>
 
       <div className="filters-card">
         <div className="search-and-clear">
           <input
             type="text"
             placeholder="بحث بالاسم أو النبذة..."
             value={networkSearchQuery}
             onChange={e => setNetworkSearchQuery(e.target.value)}
             className="search-input"
           />
           {hasActiveFilters && (
             <button onClick={handleClearNetworkFilters} className="clear-filters-btn">
               <X size={16} /> إلغاء
             </button>
           )}
         </div>
       </div>
 
       <div className="opportunities-grid">
         {loadingUsers ? (
           <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '60px' }}>
             <Loader size={36} className="spinner-icon" />
           </div>
         ) : filteredUsers.length === 0 ? (
           <div className="no-opportunities-message" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
             <p>لم يتم العثور على أعضاء</p>
           </div>
         ) : (
           filteredUsers.map(user => (
             <div key={user.id} className="opp-card">
               {/* Header: Avatar + Name */}
               <div className="opp-header-compact" style={{ gap: '12px', alignItems: 'center' }}>
                 <img
                   src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email || 'U')}&background=3b82f6&color=fff&size=80`}
                   alt={user.name}
                   style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--primary-color)' }}
                 />
                 <div style={{ minWidth: 0 }}>
                   <h3 className="opp-type-compact" style={{ fontSize: '15px' }}>{user.name || 'مستخدم'}</h3>
                   {user.subscription && (
                     <span style={{ fontSize: '11px', color: user.subscription === 'unlimited' ? '#f59e0b' : user.subscription === 'premium' ? '#8b5cf6' : 'var(--primary-color)', fontWeight: 600 }}>
                       {SUBSCRIPTION_LABELS[user.subscription] || user.subscription}
                     </span>
                   )}
                 </div>
               </div>
 
               <div className="opp-info-compact">
                 {/* Email */}
                 {user.email && (
                   <div className="info-row-compact" style={{ marginTop: '10px' }}>
                     <span className="info-label-compact">البريد:</span>
                     <span className="info-value-compact" style={{ fontSize: '12px', wordBreak: 'break-all' }}>{user.email}</span>
                   </div>
                 )}
 
                 {/* Bio */}
                 {user.bio && (
                   <div className="info-row-compact" style={{ marginTop: '6px' }}>
                     <span className="info-label-compact">نبذة:</span>
                     <span className="info-value-compact details-text">{user.bio}</span>
                   </div>
                 )}
 
                 {/* Join Date */}
                 <div className="info-row-compact" style={{ marginTop: '6px' }}>
                   <span className="info-label-compact">عضو منذ:</span>
                   <span className="info-value-compact">{user.joinDate || 'غير متوفر'}</span>
                 </div>
               </div>
 
               {/* Phone */}
               <div className="contact-section-compact" style={{ marginTop: '12px' }}>
                 {user.phoneNumber ? (
                   <div className="contact-item-compact">
                     <span className="contact-label-compact">الهاتف:</span>
                     <div className="phone-row">
                       <span className="contact-value-compact" style={{direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block'}}>
                         {(() => {
                          const raw = user.phoneNumber.startsWith('+966') ? '0' + user.phoneNumber.substring(4) : user.phoneNumber;
                          const d = raw.replace(/\D/g,'');
                          return d.length === 10 ? d.substring(0,4)+' '+d.substring(4,7)+' '+d.substring(7,10) : raw;
                        })()}
                       </span>
                       <div className="phone-buttons">
                         <button
                           className="phone-action-btn call-btn"
                           onClick={() => window.location.href = `tel:${user.phoneNumber}`}
                           title="اتصال"
                         >
                           <Phone size={14} />
                         </button>
                         <button
                           className="phone-action-btn whatsapp-btn"
                           onClick={() => window.open(`https://wa.me/${user.phoneNumber.replace(/[^\d]/g, '')}`, '_blank')}
                           title="واتساب"
                         >
                           <MessageCircle size={14} />
                         </button>
                       </div>
                     </div>
                   </div>
                 ) : (
                   <div className="contact-item-compact">
                     <span className="contact-label-compact">الهاتف:</span>
                     <span className="contact-value-compact" style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.5 }}>
                       <Lock size={14} /> غير متوفر
                     </span>
                   </div>
                 )}
               </div>
             </div>
           ))
         )}
       </div>
     </div>
   );
 }
 
 export default App;